import { propAreaSqm, sqmToRadp, sqmToAana, AANA_TO_SQM } from "../utils/areaConversions";
import { toWords, toWordsNepali, toDevanagariNum } from "../utils/numberWords";
import { buildPreliminaryHTML } from "./buildPreliminaryHTML";
import { esc } from "../utils/htmlEscape";

// Local helper: display string for area
function areaDisplay(prop) {
  if (prop.areaUnit === "sqm") {
    return `${parseFloat(prop.areaSqm)||0} sq.m`;
  }
  const { r,a,p,d } = prop.areaRadp||{};
  return `${r||0}-${a||0}-${p||0}-${d||0} (R-A-P-D)`;
}

export function buildPrintHTML(s, suggestedFilename, autoPrint = false, mapSnapshots = {}) {
  // Route to preliminary layout when report type is preliminary
  if ((s.reportType||"preliminary") === "preliminary") {
    return buildPreliminaryHTML(s, suggestedFilename, autoPrint, mapSnapshots);
  }
  // Otherwise fall through to the original (final) layout below
  const rType = (s.reportType||"preliminary").toUpperCase();

  const clientLine = (s.clients||[]).map(cl=>{
    const parts=[];
    if(cl.showPerson&&cl.person?.name) parts.push(cl.person.name);
    if(cl.showCompany&&cl.company?.name) parts.push(cl.company.name);
    return parts.join(" / ");
  }).filter(Boolean).join(", ")||"—";

  const mortProps = (s.properties||[]).filter(p=>(s.mortgagedIds||[]).includes(p.id));

  const personRows = (person) => `
    <tr><td>Name</td><td>${esc(person.name)}</td><td>Citizenship No.</td><td>${esc(person.citizenshipNo)}</td></tr>
    <tr><td>Issued Date (BS)</td><td>${esc(person.issuedDate)}</td><td>Issued By</td><td>${esc(person.issuedBy)}</td></tr>
    <tr><td>Father's Name</td><td>${esc(person.fatherName)}</td><td>Grandfather's Name</td><td>${esc(person.grandfatherName)}</td></tr>
    <tr><td>Husband's Name</td><td>${esc(person.husbandName)}</td><td>Contact</td><td>${esc(person.contact)}</td></tr>
    <tr><td>Address</td><td colspan="3">${esc(person.address)}</td></tr>`;

  // ── 3.1 Clients
  const clientsHTML = (s.clients||[]).map((cl,i)=>{
    let html = `<p class="sub-head">Client ${i+1}</p>`;
    if(cl.showPerson) html += `<table class="info-table">${personRows(cl.person||{})}</table>`;
    if(cl.showCompany){
      html += `<table class="info-table" style="margin-top:6pt">
        <tr><td>Company Name</td><td>${esc(cl.company?.name)}</td><td>PAN/VAT</td><td>${esc(cl.company?.panVat)}</td></tr>
        <tr><td>Reg. No.</td><td>${esc(cl.company?.regNo)}</td><td>Reg. Date (BS)</td><td>${esc(cl.company?.regDate)}</td></tr>
        <tr><td>Registered On</td><td colspan="3">${esc(cl.company?.regOn)}</td></tr>
        <tr><td>Contact</td><td>${esc(cl.company?.contact)}</td><td>Address</td><td>${esc(cl.company?.address)}</td></tr>
      </table>`;
      (cl.company?.directors||[]).forEach((d,di)=>{
        html+=`<p style="margin:5pt 0 2pt;font-style:italic">Director ${di+1}${d.name?" — "+esc(d.name):""}</p><table class="info-table">${personRows(d)}</table>`;
      });
    }
    return html;
  }).join(`<div style="border-top:1pt dashed #bbb;margin:8pt 0"></div>`);

  // ── 3.2 Owners
  const ownersHTML = (s.owners||[]).map((ow,i)=>{
    let html = `<p class="sub-head">Owner ${i+1}</p>`;
    if(ow.showPerson) html += `<table class="info-table">${personRows(ow.person||{})}</table>`;
    if(ow.showCompany){
      html += `<table class="info-table" style="margin-top:6pt">
        <tr><td>Company Name</td><td>${esc(ow.company?.name)}</td><td>PAN/VAT</td><td>${esc(ow.company?.panVat)}</td></tr>
        <tr><td>Reg. No.</td><td>${esc(ow.company?.regNo)}</td><td>Reg. Date (BS)</td><td>${esc(ow.company?.regDate)}</td></tr>
        <tr><td>Registered On</td><td colspan="3">${esc(ow.company?.regOn)}</td></tr>
        <tr><td>Contact</td><td>${esc(ow.company?.contact)}</td><td>Address</td><td>${esc(ow.company?.address)}</td></tr>
      </table>`;
      (ow.company?.directors||[]).forEach((d,di)=>{
        html+=`<p style="margin:5pt 0 2pt;font-style:italic">Director ${di+1}${d.name?" — "+esc(d.name):""}</p><table class="info-table">${personRows(d)}</table>`;
      });
    }
    return html;
  }).join(`<div style="border-top:1pt dashed #bbb;margin:8pt 0"></div>`);

  // ── 3.3 Property rows
  const propDetailRows = (s.properties||[]).map(p=>{
    const sqm = propAreaSqm(p);
    const {r,a,p:pp,d} = sqmToRadp(sqm);
    return `<tr>
      <td>${esc(p.plotNo)}</td><td>${esc(p.traceSheetNo)}</td><td>${esc(p.landType)}</td><td>${esc(p.category)}</td>
      <td>${p.areaUnit==="radp"?`${p.areaRadp?.r||0}-${p.areaRadp?.a||0}-${p.areaRadp?.p||0}-${p.areaRadp?.d||0}`:`${p.areaSqm||0} sq.m`}</td>
      <td>${sqm.toFixed(2)}</td><td>${r}-${a}-${pp}-${d}</td>
      <td>${esc(p.ownerName)}</td><td>${esc(p.addressLalpurja)}</td>
    </tr>`;
  }).join("");

  // ── Maps
  const mapsHTML = (s.properties||[]).map(p=>{
    const lat = parseFloat(p.lat), lng = parseFloat(p.lng);
    const hasCoords = !isNaN(lat) && !isNaN(lng);
    const googleMapsUrl = hasCoords ? `https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}` : "";
    const snap = mapSnapshots[p.id]||{};
    return (
      '<div style="margin-bottom:14pt;page-break-inside:avoid">' +
      '<p style="font-weight:bold;font-size:11pt;margin-bottom:6pt">Plot No. ' + (p.plotNo||'—') + ' &nbsp;|&nbsp; ' + (p.location||'') + ' &nbsp;|&nbsp; Plus Code: ' + (p.googlePlusCode||'—') + '</p>' +
      '<table class="info-table" style="margin-bottom:6pt"><tr>' +
        '<td>Latitude</td><td>' + (hasCoords ? lat.toFixed(6) : '—') + '</td>' +
        '<td>Longitude</td><td>' + (hasCoords ? lng.toFixed(6) : '—') + '</td>' +
        (hasCoords ? '<td><a href="' + googleMapsUrl + '" style="color:#2d6a4f;font-weight:bold">View in Google Maps</a></td>' : '<td>—</td>') +
      '</tr></table>' +
      (hasCoords
        ? '<div style="display:flex;flex-direction:column;gap:10pt">' +
            '<div style="page-break-inside:avoid">' +
              '<p style="font-size:10pt;font-weight:bold;margin-bottom:4pt;padding:3pt 8pt;background:#f5f0eb;border-left:3pt solid #f4a261">Overview Map (Area Context)</p>' +
              (snap.z15
                ? '<img src="' + snap.z15 + '" style="width:100%;height:auto;border:1pt solid #bbb;border-radius:4pt;display:block" alt="Overview map"/>'
                : '<div style="padding:20pt;border:1pt solid #ddd;text-align:center;font-style:italic;color:#888;font-size:10pt">Map not available — <a href="' + googleMapsUrl + '" style="color:#2d6a4f">View in Google Maps</a></div>') +
              '<p style="font-size:8.5pt;color:#888;text-align:right;margin-top:2pt;font-style:italic">© OpenStreetMap contributors</p>' +
            '</div>' +
            '<div style="page-break-inside:avoid">' +
              '<p style="font-size:10pt;font-weight:bold;margin-bottom:4pt;padding:3pt 8pt;background:#f5f0eb;border-left:3pt solid #f4a261">Detail Map (Property Location)</p>' +
              (snap.z18
                ? '<img src="' + snap.z18 + '" style="width:100%;height:auto;border:1pt solid #bbb;border-radius:4pt;display:block" alt="Detail map"/>'
                : '<div style="padding:20pt;border:1pt solid #ddd;text-align:center;font-style:italic;color:#888;font-size:10pt">Map not available — <a href="' + googleMapsUrl + '" style="color:#2d6a4f">View in Google Maps</a></div>') +
              '<p style="font-size:8.5pt;color:#888;text-align:right;margin-top:2pt;font-style:italic">© OpenStreetMap contributors</p>' +
            '</div>' +
          '</div>'
        : '<p style="font-style:italic;color:#888;padding:10pt;border:1pt solid #ddd;text-align:center">No coordinates entered for this property.</p>'
      ) +
      '</div>'
    );
  }).join('<div style="border-top:1pt dashed #ccc;margin:10pt 0"></div>')||`<p style="font-style:italic;color:#888">No property data.</p>`;

  // ── Mortgage + area tables
  const mortRows = mortProps.map(p=>{
    const lSqm=propAreaSqm(p);
    const measured=parseFloat(s.areaMeasured?.[p.id])||lSqm;
    const deductArea=parseFloat(s.deductions?.[p.id]?.area)||0;
    const considered=Math.max(0,Math.min(lSqm,measured)-deductArea);
    const splits = (s.plotRateSplits?.[p.id]||[]);
    const hasSplits = splits.length > 0;
    const r2=s.rates?.[p.id]||{};
    const commRate=parseFloat(r2.commercialRate)||0;
    const govRate=parseFloat(r2.govRate)||0;
    const commW=parseFloat(r2.commercialWeight)||70;
    const govW=parseFloat(r2.govWeight)||30;
    const wr=(commRate*commW/100)+(govRate*govW/100);
    const fmv=considered*wr/AANA_TO_SQM;
    const radp=sqmToRadp(considered);
    return {p,lSqm,measured,deductArea,considered,wr,fmv,radp,commRate,govRate,commW,govW,splits,hasSplits};
  });

  const consideredRows = mortRows.map(({p,lSqm,measured,deductArea,considered,radp})=>`
    <tr><td>${esc(p.plotNo)}</td><td>${lSqm.toFixed(2)}</td><td>${measured.toFixed(2)}</td><td>${deductArea.toFixed(2)}</td>
    <td><strong>${considered.toFixed(2)}</strong></td><td><strong>${radp.r}-${radp.a}-${radp.p}-${radp.d}</strong></td><td><strong>${sqmToAana(considered).toFixed(4)}</strong></td></tr>`).join("");
  const totalConsSqm = mortRows.reduce((s,r)=>s+r.considered,0);
  const totalConsRadp = sqmToRadp(totalConsSqm);
  const consideredTotalRow = mortRows.length?`<tr class="total-row"><td colspan="4"><strong>TOTAL CONSIDERED AREA</strong></td><td><strong>${totalConsSqm.toFixed(2)}</strong></td><td><strong>${totalConsRadp.r}-${totalConsRadp.a}-${totalConsRadp.p}-${totalConsRadp.d}</strong></td><td><strong>${sqmToAana(totalConsSqm).toFixed(4)}</strong></td></tr>`:"";

  const deductRows = mortRows.map(({p})=>`
    <tr><td>${esc(p.plotNo)}</td><td>${esc(s.deductions?.[p.id]?.dim)}</td><td>${s.deductions?.[p.id]?.area||"0"}</td><td>${esc(s.deductions?.[p.id]?.reason)}</td></tr>`).join("");

  const roadAccessHTML=(s.properties||[]).map(p=>{
    const rows=(s.roadAccess?.[p.id]||[]);
    if(!rows.length) return `<tr><td>${esc(p.plotNo)}</td><td colspan="5" style="font-style:italic;color:#999">No access rows entered</td></tr>`;
    return rows.map(r=>`<tr><td>${esc(p.plotNo)}</td><td>${esc(r.roadType)}</td><td>${esc(r.frontage)}</td><td>${esc(r.widthField)}</td><td>${esc(r.widthTrace)}</td><td>${esc(r.remarks)}</td></tr>`).join("");
  }).join("");

  const boundaryRows = mortRows.map(({p})=>{
    const ac=s.access?.[p.id]||{};
    return `<tr><td>${esc(p.plotNo)}</td><td>${esc(ac.east)}</td><td>${esc(ac.west)}</td><td>${esc(ac.north)}</td><td>${esc(ac.south)}</td><td>${esc(ac.remarks)}</td></tr>`;
  }).join("");

  // ── Land valuation
  let totalLandComm=0, totalLandFMV2=0;
  const landValRows = mortRows.map(({p,considered,commRate,govRate,commW,govW,wr,fmv,radp,splits,hasSplits})=>{
    if(hasSplits){
      let plotComm=0,plotFmv=0;
      const zoneRows = splits.map(sp=>{
        const spArea=parseFloat(sp.areaSqm)||0;
        const spCRate=parseFloat(sp.commercialRate)||0;
        const spCW=sp.commercialWeight!==undefined?parseFloat(sp.commercialWeight):70;
        const spGW=sp.govWeight!==undefined?parseFloat(sp.govWeight):30;
        const spFmvRate=(spCRate*spCW/100)+((parseFloat(sp.govRate)||0)*spGW/100);
        const spCVal=spArea*spCRate/AANA_TO_SQM;
        const spFVal=spArea*spFmvRate/AANA_TO_SQM;
        plotComm+=spCVal; plotFmv+=spFVal;
        return `<tr><td style="padding-left:16pt;font-style:italic;color:#555">${esc(p.plotNo)} (${esc(sp.label, "Zone")})</td>
          <td>${spArea.toFixed(2)}</td><td>${sqmToAana(spArea).toFixed(4)}</td>
          <td>${spCRate.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
          <td>${spFmvRate.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
          <td>NPR ${spCVal.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
          <td>NPR ${spFVal.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>`;
      }).join("");
      const cR=Math.floor(plotComm/100)*100, fR=Math.floor(plotFmv/100)*100;
      totalLandComm+=cR; totalLandFMV2+=fR;
      return `${zoneRows}
        <tr style="background:#f0ece6;font-weight:bold"><td>${esc(p.plotNo)} — Sub-total</td><td>${considered.toFixed(2)}</td><td>${sqmToAana(considered).toFixed(4)}</td><td colspan="2" style="text-align:center;font-style:italic;color:#888">Multiple rates</td><td>NPR ${plotComm.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td><td>NPR ${plotFmv.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr style="background:#e8f5e9"><td colspan="5" style="font-size:9.5pt;color:#555;padding-left:14pt"><em>Rounded Down (nearest 100)</em></td><td><strong style="color:#2e7d32">NPR ${cR.toLocaleString("en-NP")}</strong></td><td><strong style="color:#2e7d32">NPR ${fR.toLocaleString("en-NP")}</strong></td></tr>`;
    }
    const cVal=considered*commRate/AANA_TO_SQM;
    const cR=Math.floor(cVal/100)*100, fR=Math.floor(fmv/100)*100;
    totalLandComm+=cR; totalLandFMV2+=fR;
    return `<tr><td>${esc(p.plotNo)}</td><td>${considered.toFixed(2)}</td><td>${sqmToAana(considered).toFixed(4)}</td>
      <td>${commRate.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
      <td>${wr.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
      <td>NPR ${cVal.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
      <td>NPR ${fmv.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
    <tr style="background:#e8f5e9"><td colspan="5" style="font-size:9.5pt;color:#555;padding-left:14pt"><em>Rounded Down (nearest 100)</em></td>
      <td><strong style="color:#2e7d32">NPR ${cR.toLocaleString("en-NP")}</strong></td>
      <td><strong style="color:#2e7d32">NPR ${fR.toLocaleString("en-NP")}</strong></td></tr>`;
  }).join("");

  // ── Buildings detail + valuation
  const bv = s.buildingVals||{};
  let totalBuildingVal=0;
  const buildingsDetailHTML = (s.buildings||[]).map((b,i)=>{
    const totalActual=(b.areaTable||[]).reduce((sum,a)=>sum+(parseFloat(a.areaActual)||0),0);
    const totalApproved=(b.areaTable||[]).reduce((sum,a)=>sum+(parseFloat(a.areaApproved)||0),0);
    const totalCert=(b.areaTable||[]).reduce((sum,a)=>sum+(parseFloat(a.areaCertificate)||0),0);
    const areaRows=(b.areaTable||[]).map(a=>`<tr><td>${esc(a.description)}</td><td>${parseFloat(a.areaActual)||0}</td><td>${parseFloat(a.areaApproved)||0}</td><td>${parseFloat(a.areaCertificate)||0}</td></tr>`).join("");
    const v=bv[b.id]||{}, floorRates=v.floorRates||{};
    const floorCalcs=(b.areaTable||[]).map(row=>({description:row.description||"—",area:parseFloat(row.areaActual)||0,rate:parseFloat(floorRates[row.id])||0,cost:(parseFloat(row.areaActual)||0)*(parseFloat(floorRates[row.id])||0)}));
    const baseCost=floorCalcs.reduce((s,f)=>s+f.cost,0);
    const totalArea=floorCalcs.reduce((s,f)=>s+f.area,0);
    const sanCost=baseCost*(parseFloat(v.sanitaryPct)||0)/100;
    const elecCost=baseCost*(parseFloat(v.electricalPct)||0)/100;
    const finCost=baseCost*(parseFloat(v.finishingPct)||0)/100;
    const totalWithFix=baseCost+sanCost+elecCost+finCost;
    const age=parseFloat(b.ageOfBuilding)||0, depRate=parseFloat(v.depreciationRate)||2.25;
    const totalDepPct=Math.min(100,age*depRate), totalDep=totalWithFix*totalDepPct/100;
    const actual=Math.max(0,totalWithFix-totalDep), rounded=Math.floor(actual/100)*100;
    totalBuildingVal+=rounded;
    const floorRows=floorCalcs.map(f=>`<tr><td>${esc(f.description)}</td><td>${f.area.toFixed(2)}</td><td>${f.rate.toLocaleString("en-NP")}</td><td>${f.cost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>`).join("");
    return ("" + `    <p class="sub-head">Building ${i+1}${b.plotNo?` — Plot No. ${esc(b.plotNo)}`:""}</p>
    <table class="info-table">
      <tr><td>Owner</td><td>${esc(b.ownerName)}</td><td>Plot No.</td><td>${esc(b.plotNo)}</td></tr>
      <tr><td>Face Direction</td><td>${esc(b.faceDirection)}</td><td>No. of Floors</td><td>${esc(b.numFloors)}</td></tr>
      <tr><td>Permitted Floors</td><td>${esc(b.floorPermission)}</td><td>Year of Const.</td><td>${esc(b.yearOfConstruction)}</td></tr>
      <tr><td>Completion Date (BS)</td><td>${esc(b.completionDate)}</td><td>Age (Years)</td><td>${esc(b.ageOfBuilding)}</td></tr>
      <tr><td>Expected Life (Years)</td><td>${esc(b.expectedLife)}</td><td>Structure Type</td><td>${esc(b.structureType)}</td></tr>
      <tr><td>Foundation Type</td><td colspan="3">${esc(b.foundationType)}</td></tr>
    </table>
    <p style="font-weight:bold;font-size:11pt;margin:7pt 0 4pt">Building Area Details (sq.ft):</p>
    <table>
      <thead><tr><th>Description</th><th>Actual Construction</th><th>Approved Map</th><th>Completion Cert.</th></tr></thead>
      <tbody>${areaRows||`<tr><td colspan="4" style="text-align:center;font-style:italic">No areas entered</td></tr>`}
        <tr class="total-row"><td><strong>TOTAL</strong></td><td><strong>${totalActual.toFixed(2)}</strong></td><td><strong>${totalApproved.toFixed(2)}</strong></td><td><strong>${totalCert.toFixed(2)}</strong></td></tr>
      </tbody>
    </table>
    <p style="font-weight:bold;font-size:11pt;margin:7pt 0 4pt">Building Valuation:</p>
    <table>
      <thead><tr><th>Floor</th><th>Area (sq.ft)</th><th>Rate (NPR/sq.ft)</th><th>Floor Cost (NPR)</th></tr></thead>
      <tbody>${floorRows}
        <tr class="total-row"><td colspan="3"><strong>Total Base Construction Cost</strong></td><td><strong>${baseCost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></td></tr>
      </tbody>
    </table>
    <table style="margin-top:4pt">
      <thead><tr><th>Description</th><th>Calculation</th><th>Amount (NPR)</th></tr></thead>
      <tbody>
        <tr><td>Base Construction Cost</td><td>${totalArea.toFixed(2)} sq.ft</td><td>${baseCost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr><td>Sanitary Cost</td><td>${parseFloat(v.sanitaryPct)||0}% of base</td><td>${sanCost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr><td>Electrical Cost</td><td>${parseFloat(v.electricalPct)||0}% of base</td><td>${elecCost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr><td>Finishing Cost</td><td>${parseFloat(v.finishingPct)||0}% of base</td><td>${finCost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr style="background:#f0e8d8"><td><strong>Total Cost (with fixtures)</strong></td><td></td><td><strong>${totalWithFix.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></td></tr>
        <tr><td>Depreciation</td><td>${age} yr × ${depRate}% = ${totalDepPct.toFixed(2)}%</td><td>− ${totalDep.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr class="total-row"><td colspan="2"><strong>Actual Cost (Depreciated)</strong></td><td><strong>NPR ${actual.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></td></tr>
        <tr style="background:#e8f5e9"><td><strong>Rounded Down (nearest 100)</strong></td><td style="font-size:9.5pt;color:#666">floor(÷100)×100</td><td><strong style="color:#2e7d32">NPR ${rounded.toLocaleString("en-NP")}</strong></td></tr>
      </tbody>
    </table>`);
  }).join('<div style="border-top:1pt dashed #aaa;margin:10pt 0"></div>');

  // ── Summary values
  const finalComm = Math.floor((totalLandComm + totalBuildingVal) / 100) * 100;
  const finalFMV  = Math.floor((totalLandFMV2 + totalBuildingVal) / 100) * 100;
  const distressMult2 = Math.min(100, Math.max(0, parseFloat(s.distressPct)||80)) / 100;
  const distress  = Math.floor(finalFMV * distressMult2 / 100) * 100;
  // Government value
  const govVals2 = s.govValues || {};
  const totalGovFinal = (s.properties||[]).filter(p=>(s.mortgagedIds||[]).includes(p.id)).reduce((sum,p)=>{
    const r2 = (s.rates||{})[p.id]||{};
    const splits2 = ((s.plotRateSplits||{})[p.id]||[]);
    const gRate = splits2.length > 0
      ? (parseFloat(splits2[0].govRate)||0)
      : (parseFloat(r2.govRate)||0);
    const am = parseFloat((s.areaMeasured||{})[p.id])||0;
    const lSqm = (()=>{if(p.areaUnit==='sqm')return parseFloat(p.areaSqm)||0;const r=p.areaRadp||{};return(parseFloat(r.r)||0)*508.72+(parseFloat(r.a)||0)*31.795+(parseFloat(r.p)||0)*7.949+(parseFloat(r.d)||0)*1.987;})();
    const ded = parseFloat(((s.deductions||{})[p.id]||{}).area)||0;
    const ca = Math.max(0,(am||lSqm)-ded);
    return sum + gRate*(ca/31.795);
  },0);
  const finalGovFinal = Math.floor(totalGovFinal/100)*100;

  // ── Extra fields
  const vi = s.valuatorInfo||{};
  const photoList = s.photos||[];

  // Per-property description — supports new propDescriptions map + legacy single propDescription
  const propDescs = s.propDescriptions || {};
  const getLegacyPd = (pid) => {
    if (propDescs[pid]) return propDescs[pid];
    const props0 = s.properties||[];
    if (props0[0]?.id === pid && s.propDescription && Object.values(s.propDescription).some(Boolean)) return s.propDescription;
    return {};
  };

  const propDescHTML = (() => {
    const props = s.properties||[];
    if (!props.length) return '<p style="font-style:italic;color:#888">No properties entered.</p>';
    return props.map((prop, pi) => {
      const pd = getLegacyPd(prop.id);
      const hasData = pd.shape||pd.topography||pd.presentCondition||pd.occupancy||pd.surroundings||pd.description;
      const header = props.length > 1
        ? '<div style="margin:8pt 0 4pt;font-weight:bold;color:#2c1810;font-size:11pt">Property '+(pi+1)+(prop.plotNo?' — Plot No. '+esc(prop.plotNo):'')+(prop.ownerName?' ('+esc(prop.ownerName)+')':'')+'</div>'
        : '';
      const styleKH = 'font-weight:bold;text-transform:uppercase;font-size:9.5pt;background:#f5f0eb;width:22%';
      const styleK  = 'font-weight:bold;text-transform:uppercase;font-size:9.5pt;background:#f5f0eb';
      const table = hasData
        ? '<table class="info-table">'
          + '<tr><td style="'+styleKH+'">Shape of Land</td><td>'+esc(pd.shape)+'</td><td style="'+styleKH+'">Topography</td><td>'+esc(pd.topography)+'</td></tr>'
          + '<tr><td style="'+styleK+'">Present Condition</td><td>'+esc(pd.presentCondition)+'</td><td style="'+styleK+'">Occupancy</td><td>'+esc(pd.occupancy)+'</td></tr>'
          + '<tr><td style="'+styleK+'">Surroundings</td><td colspan="3">'+esc(pd.surroundings)+'</td></tr>'
          + (pd.description ? '<tr><td style="'+styleK+'">Description</td><td colspan="3">'+esc(pd.description)+'</td></tr>' : '')
          + '</table>'
        : '<p style="font-style:italic;color:#888;padding:6pt 0">No description entered for this property.</p>';
      return header + table;
    }).join('');
  })();

  const autoScript = autoPrint ? `<script>window.addEventListener('load',function(){setTimeout(function(){window.focus();window.print();},600);});<\/script>` : "";

  // ── Runtime DOMPurify sanitization (guards against any stored-XSS that slipped through) ──
  const purifyScript = `<script>
  window.addEventListener('load', function() {
    if (typeof DOMPurify === 'undefined') return;
    var els = document.querySelectorAll('td, p, li, span, div');
    els.forEach(function(el) {
      if (el.children.length === 0 && el.innerHTML !== el.textContent) {
        el.innerHTML = DOMPurify.sanitize(el.innerHTML, { ALLOWED_TAGS: ['strong','em','br','b','i'] });
      }
    });
  });
  <\/script>`;

  // ── TOC page-number updater script ──
  const tocScript = `<script>
  (function() {
    // Maps TOC entry number → actual HTML anchor id
    var ANCHOR_MAP = {
      '1':  'ch-1',
      '2':  'ch-2',
      '3':  'toc-s3',
      '4':  'ch-3',
      '5':  'toc-s5',
      '6':  'ch-4',
      '7':  'toc-s7',
      '8':  'toc-s8',
      '9':  'ch-6',
      '10': 'ch-7',
      '11': 'toc-s11',
      '12': 'toc-s12'
    };

    function updateToc() {
      // Measure A4 page height dynamically from the first page-break element
      // Fall back to 297mm at current devicePixelRatio-aware px
      var mmToPx = function(mm) { return mm * (96 / 25.4); };
      var A4_H = mmToPx(297);

      // Count pre-body pages: cover (1) + letterhead (1 if present) + TOC (1)
      var hasCover = !!document.querySelector('.cover-page, .letter-page');
      var hasToc   = !!document.querySelector('.toc-page');
      var PRE_PAGES = (hasCover ? 2 : 0) + (hasToc ? 1 : 0);

      // Subtract the on-screen toolbar height (it's not part of the printed page)
      var toolbar = document.querySelector('.no-print');
      var toolbarH = toolbar ? toolbar.getBoundingClientRect().height : 0;

      Object.keys(ANCHOR_MAP).forEach(function(num) {
        var anchorId = ANCHOR_MAP[num];
        var anchor   = document.getElementById(anchorId);
        var cell     = document.getElementById('toc-pg-' + num);
        if (!anchor || !cell) return;
        var top = anchor.getBoundingClientRect().top + window.scrollY - toolbarH;
        var pg  = Math.max(1, Math.floor(top / A4_H) + 1 + PRE_PAGES);
        cell.textContent = pg;
      });
    }

    function runWhenReady() {
      // Run once now, then again after all images finish loading for accuracy
      updateToc();
      var imgs = document.images;
      var pending = 0;
      for (var i = 0; i < imgs.length; i++) {
        if (!imgs[i].complete) {
          pending++;
          imgs[i].addEventListener('load',  function() { if (--pending === 0) updateToc(); });
          imgs[i].addEventListener('error', function() { if (--pending === 0) updateToc(); });
        }
      }
      // Final safety pass after everything settles
      setTimeout(updateToc, 1200);
    }

    if (document.readyState === 'complete') { runWhenReady(); }
    else { window.addEventListener('load', runWhenReady); }
  })();
  <\/script>`;

  // ── Letterhead: use raw PNG data URL from state (set by prepareFinalState) ──
  // Only rendered on the cover letter page — nowhere else in the report.
  const lhSrc = s.letterheadPng || "";

  // ── Text box positioning from admin-defined box (% of A4 page) ──
  // The super admin drags a box on the A4 preview; coords are stored as % of the page.
  const tb = s.letterheadTextBox;
  const contentStyle = tb
    ? `position:absolute;top:${tb.top}%;left:${tb.left}%;width:${tb.width}%;height:${tb.height}%;box-sizing:border-box;overflow:auto;z-index:1;padding:2mm;`
    : `position:relative;z-index:1;padding:18mm 18mm 14mm 22mm;box-sizing:border-box;`;

  // ── Watermark region: a specific area of the letterhead shown at low opacity ──
  // The super admin drags a second box to define the watermark zone.
  // The same PNG is clipped to that region and rendered at the configured opacity.
  const wb = s.letterheadWatermarkBox; // {top,left,width,height,opacity}
  const wmHtml = (lhSrc && wb)
    ? `<div style="position:absolute;top:${wb.top}%;left:${wb.left}%;width:${wb.width}%;height:${wb.height}%;overflow:hidden;z-index:0;pointer-events:none;-webkit-print-color-adjust:exact;print-color-adjust:exact;">
        <img src="${lhSrc}" alt="" style="position:absolute;top:${-(wb.top)}%;left:${-(wb.left)}%;width:${(100/wb.width)*100}%;height:${(100/wb.height)*100}%;object-fit:fill;opacity:${wb.opacity||0.12};-webkit-print-color-adjust:exact;print-color-adjust:exact;pointer-events:none;"/>
      </div>`
    : "";

  // ── Pre-build photo pages (avoid nested template literal issues) ──
  const final_photoPages = (() => {
    const photos = (s.photos||[]).filter(p => p && (p.dataUrl||p.data));
    if (!photos.length) return "";
    const PER_PAGE = 4;
    const pages = [];
    for (let i = 0; i < photos.length; i += PER_PAGE) pages.push(photos.slice(i, i + PER_PAGE));
    const separator = "<div class=\"photo-sep\" id=\"toc-s11\">" +
      "<div style=\"text-align:center;font-family:'Times New Roman',Times,serif;\">" +
        "<div style=\"width:80mm;height:3pt;background:#2c1810;margin:0 auto 12pt;\"></div>" +
        "<div style=\"font-size:22pt;font-weight:bold;text-transform:uppercase;letter-spacing:4px;color:#2c1810;\">Property Photographs</div>" +
        "<div style=\"font-size:11pt;color:#666;margin-top:6pt;letter-spacing:1px;\">" + photos.length + " photograph" + (photos.length !== 1 ? "s" : "") + "</div>" +
        "<div style=\"width:80mm;height:3pt;background:#2c1810;margin:12pt auto 0;\"></div>" +
      "</div></div>";
    return separator + pages.map((group, pi) => {
      const cells = group.map((ph, ci) => {
        const num = pi * PER_PAGE + ci + 1;
        const capRaw = ph.caption || ph.label || ("Photo " + num);
        const capEsc = esc(capRaw);
        const src = ph.dataUrl || ph.data || "";
        return "<div class=\"photo-cell\"><img src=\"" + src + "\" class=\"photo-img\" alt=\"" + capEsc + "\"/><div class=\"photo-caption\">" + num + ". " + capEsc + "</div></div>";
      }).join("");
      const pad = Array(PER_PAGE - group.length).fill("<div class=\"photo-cell photo-cell-empty\"></div>").join("");
      const pageNum = pi + 1;
      const pageTotal = pages.length;
      return "<div class=\"photo-page\">" +
        "<div class=\"photo-page-header\"><span>Property Photographs</span><span>Page " + pageNum + " of " + pageTotal + "</span></div>" +
        "<div class=\"photo-grid-4\">" + cells + pad + "</div>" +
        "</div>";
    }).join("");
  })();

  // ── Table of contents entries ──
  const tocSections = [
    ["1",  "General Information — Bank, Dates & Parties"],
    ["2",  "Property Description & Physical Characteristics"],
    ["3",  "Location Maps"],
    ["4",  "Area to be Mortgaged & Considered Area"],
    ["5",  "Access to Property & Boundary Declaration"],
    ["6",  "Building Details & Technical Specifications"],
    ["7",  "Valuation of Land"],
    ["8",  "Valuation of Building"],
    ["9",  "Summary of Valuation"],
    ["10", "Remarks & Limiting Conditions"],
    ["11", "Property Photographs"],
    ["12", "Legal Documents"],
  ];

  // TOC rows — page numbers filled by JS after render
  const tocRows = tocSections.map(([num, title]) =>
    `<tr id="toc-row-${num}">
      <td style="padding:5pt 10pt;border:0.5pt solid #ddd;width:10%;font-weight:bold;color:#2c1810">${num}</td>
      <td style="padding:5pt 10pt;border:0.5pt solid #ddd">${title}</td>
      <td style="padding:5pt 10pt;border:0.5pt solid #ddd;width:10%;text-align:right;font-weight:600;color:#2c1810" id="toc-pg-${num}">—</td>
    </tr>`
  ).join("");
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'self' 'unsafe-inline' data: blob:; script-src 'self' 'unsafe-inline' https://cdnjs.cloudflare.com;">
  <title>${suggestedFilename||"Final Valuation Report"}</title>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/dompurify/3.0.6/purify.min.js"></script>
  <style>
    @page {
      size: A4 portrait;
      margin: 15mm 15mm 18mm 20mm;
    }
    @page :first { margin-top: 0; }
    *,*::before,*::after { box-sizing:border-box; margin:0; padding:0; }
    body { font-family:"Times New Roman",Times,serif; font-size:11.5pt; line-height:1.55; color:#111; background:#fff; }

    /* Running header/footer */
    @page { @top-right { content: "Neo-Civic Consulting Pvt. Ltd"; font-family:"Times New Roman",Times,serif; font-size:8.5pt; color:#888; } @bottom-left { content: "Page " counter(page); font-family:"Times New Roman",Times,serif; font-size:8.5pt; color:#555; font-weight:bold; } @bottom-right { content: "${suggestedFilename||"Final Valuation Report"}"; font-family:"Times New Roman",Times,serif; font-size:7.5pt; color:#aaa; } }
    @page cover-p  { @top-right { content: ""; } @bottom-center { content: ""; } @bottom-right { content: ""; } }
    @page letter-p { size:A4 portrait; margin:8mm 14mm 8mm 14mm; @top-right { content: ""; } @bottom-center { content: ""; } @bottom-right { content: ""; } }
    @page toc-p    { @top-right { content: ""; } @bottom-center { content: ""; } @bottom-right { content: ""; } }

    /* Screen toolbar */
    .no-print { display:flex; align-items:center; gap:10px; padding:10px 20px; background:#1a1714; color:#fff;
      font-family:Arial,sans-serif; font-size:13px; position:sticky; top:0; z-index:9999; box-shadow:0 2px 8px rgba(0,0,0,0.3);
      print-color-adjust:exact; }
    .no-print button { padding:7px 20px; border:none; border-radius:5px; font-weight:700; cursor:pointer; font-size:13px; }
    .no-print .btn-print { background:#c0392b; color:#fff; }
    .no-print .btn-html  { background:#2d6a4f; color:#fff; }
    .no-print .btn-close { background:rgba(255,255,255,0.15); color:#fff; border:1px solid rgba(255,255,255,0.3); }
    @media print {
      .no-print { display:none !important; height:0 !important; overflow:hidden !important; }
      html, body { background:white !important; margin:0 !important; padding:0 !important; }
      body { print-color-adjust:exact; -webkit-print-color-adjust:exact; }
    }

    .rh { display:none; }
    @media print { .rh { display:block; } body { counter-reset: page-counter; } }

    /* Page wrapper */
    .page-wrap { max-width:175mm; margin:0 auto; padding:0 0 10mm; }
    @media print { .page-wrap { max-width:100%; margin:0; padding:0; } }

    /* Cover/Letter pages */
    @page cover-p  { size:210mm 297mm; margin:0; }
    @page letter-p { size:210mm 297mm; margin:0; }
    @page toc-p    { size:210mm 297mm; margin:18mm 18mm 18mm 22mm; }
    .cover-page  { page:cover-p; page-break-after:always; width:210mm; height:297mm; min-height:297mm; max-height:297mm; box-sizing:border-box; display:flex; flex-direction:column; font-family:"Times New Roman",Times,serif; background:#fff; overflow:hidden; }
    .letter-page { page:letter-p; page-break-after:always; font-family:"Times New Roman",Times,serif; font-size:9.5pt; line-height:1.38; box-sizing:border-box; overflow:hidden; width:210mm; min-height:297mm; height:297mm; position:relative; background:#fff; }
    .letter-page-bg { position:absolute; top:0; left:0; width:100%; height:100%; z-index:0; pointer-events:none; -webkit-print-color-adjust:exact; print-color-adjust:exact; background:#fff; }
    .letter-page-bg img { width:100%; height:100%; object-fit:contain; object-position:top center; display:block; opacity:1; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    .letter-page-content { position:relative; z-index:1; padding:18mm 18mm 14mm 22mm; box-sizing:border-box; min-height:297mm; display:flex; flex-direction:column; }
    .toc-page    { page:toc-p;    page-break-after:always; font-family:"Times New Roman",Times,serif; }

    /* Cover */
    .cv-topbar   { background:#2c1810; height:16mm; width:100%; }
    .cv-lh-wrap  { width:100%; background:#fff; }
    .cv-lh-wrap img { width:100%; display:block; }
    .cv-body     { flex:1; display:flex; flex-direction:column; align-items:center; padding:8mm 20mm 0; }
    .cv-divider  { width:70mm; height:2.5pt; background:#2c1810; margin:6mm auto; }
    .cv-badge    { border:2pt solid #2c1810; padding:6mm 14mm; text-align:center; background:#fff9f6; width:100%; }
    .cv-badge h1 { font-size:19pt; font-weight:bold; text-transform:uppercase; letter-spacing:2px; color:#2c1810; margin-bottom:3mm; }
    .cv-badge h2 { font-size:11.5pt; font-weight:normal; color:#444; }
    .cv-badge-label { display:inline-block; background:#2c1810; color:#fff; font-size:11pt; font-weight:bold; letter-spacing:3px; text-transform:uppercase; padding:3pt 18pt; margin-top:5mm; }
    .cv-meta     { width:100%; border-collapse:collapse; margin-top:8mm; font-size:11pt; }
    .cv-meta td  { padding:5pt 8pt; border:0.75pt solid #ccc; }
    .cv-meta td:nth-child(odd)  { background:#f5f0eb; font-weight:bold; width:36%; text-transform:uppercase; font-size:9.5pt; color:#2c1810; letter-spacing:0.3px; }
    .cv-foot     { background:#2c1810; padding:4mm 20mm; display:flex; justify-content:space-between; align-items:center; font-size:9pt; color:#fff; font-family:"Times New Roman",Times,serif; }

    /* Cover letter */
    .lh-bar  { width:100%; margin-bottom:14pt; }
    .lh-bar img { width:100%; display:block; }
    .to-block  { margin-bottom:10pt; font-size:11.5pt; line-height:1.8; }
    .ltr-subj  { font-weight:bold; text-decoration:underline; font-size:12pt; margin:10pt 0 14pt; }
    .ltr-body p { margin-bottom:9pt; text-align:justify; font-size:11.5pt; }
    .ltr-sumtable { width:100%; border-collapse:collapse; margin:8pt 0 12pt; font-size:11pt; }
    .ltr-sumtable th { background:#2c1810; color:#fff; padding:5pt 10pt; text-align:left; border:1pt solid #2c1810; }
    .ltr-sumtable td { padding:5pt 10pt; border:0.75pt solid #bbb; }
    .ltr-signoff { margin-top:20pt; }
    .sig-rule    { border-top:1pt solid #000; width:55mm; margin-top:22pt; }

    /* TOC */
    .toc-title   { font-size:16pt; font-weight:bold; text-align:center; text-transform:uppercase; letter-spacing:2px; color:#2c1810; margin-bottom:5mm; padding-bottom:4pt; border-bottom:2pt solid #2c1810; }
    .toc-lh { width:100%; margin-bottom:8mm; }
    .toc-lh img { width:100%; }
    .toc-table { width:100%; border-collapse:collapse; font-size:11.5pt; margin-top:4mm; }

    /* Section headings */
    .ch-head { background:#2c1810; color:#fff; font-size:12.5pt; font-weight:bold; text-transform:uppercase; letter-spacing:1.5px; padding:7pt 14pt; margin:20pt 0 10pt; page-break-after:avoid; border-left:6pt solid #f4a261; display:flex; align-items:center; gap:8pt; }
    .sec-h3  { font-size:11.5pt; font-weight:bold; color:#2c1810; padding:4pt 10pt 4pt 12pt; margin:12pt 0 6pt; border-left:4pt solid #f4a261; background:#faf6f3; page-break-after:avoid; letter-spacing:0.3px; }
    .sub-head { font-size:11.5pt; font-weight:bold; border-bottom:1.5pt solid #2c1810; padding-bottom:3pt; margin:10pt 0 5pt; color:#2c1810; }
    .sec-num-badge { display:inline-block; background:#f4a261; color:#2c1810; font-size:10pt; font-weight:bold; padding:1pt 8pt; border-radius:2pt; margin-right:4pt; }

    /* Info tables */
    table    { width:100%; border-collapse:collapse; margin-bottom:8pt; font-size:11pt; }
    th       { background:#2c1810; color:#fff; padding:5pt 7pt; text-align:left; font-size:10pt; font-weight:bold; letter-spacing:0.3px; border:1pt solid #2c1810; }
    td       { padding:4pt 7pt; border:0.75pt solid #bbb; vertical-align:top; }
    tr:nth-child(even) td { background:#faf7f5; }
    .info-table td            { border:0.75pt solid #ccc; }
    .info-table td:first-child,.info-table td:nth-child(3) { background:#f5f0eb; font-weight:bold; width:22%; font-size:9.5pt; text-transform:uppercase; color:#2c1810; letter-spacing:0.2px; vertical-align:middle; }
    .total-row td             { background:#2c1810 !important; color:#fff !important; font-weight:bold; font-size:11.5pt; }
    .sub-total-row td         { background:#e8e0d8 !important; font-weight:bold; }
    table tr:hover td         { background:#fff8f4; }

    /* Valuation summary box */
    .val-summary { border:2pt solid #2c1810; margin:12pt 0; }
    .val-summary-head { background:#2c1810; color:#fff; text-align:center; font-size:13pt; font-weight:bold; padding:6pt; letter-spacing:1px; text-transform:uppercase; }
    .val-row      { display:flex; justify-content:space-between; align-items:center; padding:8pt 14pt; border-bottom:0.5pt solid #ddd; }
    .val-row:last-child { border-bottom:none; }
    .val-row.a    { background:#e8f4ef; }
    .val-row.b    { background:#fff5ee; }
    .val-row.c    { background:#ffeaea; }
    .val-row .vlbl { font-weight:bold; font-size:11.5pt; }
    .val-row .vamt { font-weight:bold; font-size:14pt; color:#2c1810; }
    .in-words     { border:1.5pt solid #bbb; padding:6pt 10pt; margin:4pt 0 10pt; font-style:italic; font-size:11pt; background:#fffdf9; }

    /* Misc */
    .remarks-box  { border:1pt solid #bbb; padding:8pt 10pt; min-height:32pt; white-space:pre-wrap; font-size:11.5pt; background:#fffdf9; margin-bottom:8pt; }
    .disclaimer   { font-size:9pt; color:#666; font-style:italic; border-top:0.5pt solid #ccc; padding-top:7pt; margin-top:12pt; }
    .page-break   { page-break-before:always; }
    .no-break     { page-break-inside:avoid; }
    .dashed-sep   { border-top:1pt dashed #bbb; margin:8pt 0; }

    /* Landscape photo pages */
    @page photo-p { size:A4 landscape; margin:10mm 12mm; }
    .photo-page { page:photo-p; page-break-before:always; page-break-inside:avoid; width:100%; box-sizing:border-box; }
    .photo-page-lh { display:none; }
    .photo-sep { page:photo-p; page-break-before:always; display:flex; align-items:center; justify-content:center; min-height:180mm; }

    /* Landscape legal document pages */
    @page legal-doc-p { size:A4 landscape; margin:10mm 14mm; }
    .legal-doc-page { page:legal-doc-p; page-break-before:always; page-break-inside:avoid; width:100%; box-sizing:border-box; }
    .legal-doc-sep  { page:legal-doc-p; page-break-before:always; display:flex; align-items:center; justify-content:center; min-height:180mm; }

    .photo-page-header { display:flex; justify-content:space-between; align-items:center; font-family:"Times New Roman",Times,serif; font-size:10pt; font-weight:bold; border-top:1.5pt solid #1a3a5c; border-bottom:0.75pt solid #1a3a5c; padding:4pt 0; margin-bottom:8pt; color:#1a3a5c; text-transform:uppercase; letter-spacing:0.4px; }
    .photo-grid-4 { display:grid; grid-template-columns:1fr 1fr; grid-template-rows:1fr 1fr; gap:8pt; height:175mm; }
    .photo-cell   { display:flex; flex-direction:column; border:0.75pt solid #ccc; border-radius:2pt; overflow:hidden; background:#fafafa; page-break-inside:avoid; }
    .photo-cell-empty { background:#f5f5f5 !important; border:0.75pt dashed #ddd !important; }
    .photo-img    { flex:1; width:100%; object-fit:cover; display:block; min-height:0; }
    .photo-caption{ font-family:"Times New Roman",Times,serif; font-size:8.5pt; font-style:italic; text-align:center; padding:2pt 4pt; background:#eef2f7; border-top:0.5pt solid #ddd; color:#333; flex-shrink:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    @media screen {
      .photo-page { background:white; max-width:277mm; margin:20px auto; padding:10mm 12mm; box-shadow:0 2px 12px rgba(0,0,0,0.12); }
      .legal-doc-page { background:white; max-width:277mm; margin:20px auto; padding:10mm 14mm; box-shadow:0 2px 12px rgba(0,0,0,0.12); }
      .photo-grid-4 { height:auto; }
      .photo-img  { height:170px; }
    }
    @media print {
      html, body { background:white !important; margin:0 !important; padding:0 !important; }
      .photo-page { box-shadow:none !important; margin:0 !important; padding:0 !important; }
      .legal-doc-page { box-shadow:none !important; margin:0 !important; padding:0 !important; }
      .photo-grid-4 { height:175mm; }
      .letter-page { font-size:9.5pt !important; }
      .no-print { display:none !important; height:0 !important; }
      body { print-color-adjust:exact; -webkit-print-color-adjust:exact; }
    }

    /* Cover/letter screen shadow */
    @media screen {
      .cover-page  { box-shadow:0 4px 20px rgba(0,0,0,0.15); margin:20px auto; max-width:210mm; }
      .letter-page { box-shadow:0 2px 12px rgba(0,0,0,0.1); margin:20px auto; max-width:210mm; }
      .toc-page    { box-shadow:0 2px 12px rgba(0,0,0,0.1); margin:20px auto; max-width:175mm; padding:18mm; }
      .page-wrap   { box-shadow:0 2px 12px rgba(0,0,0,0.08); margin:20px auto; padding:18mm; background:white; }
    }
  </style></head>
  <body>
  <div class="no-print">
    <span style="font-weight:600;flex:1">📄 ${esc(suggestedFilename, "Final Valuation Report")}</span>
    <button class="btn-print" onclick="window.print()" title="Or press Ctrl+P / ⌘P">🖨️ Ctrl+P to Print / Save as PDF</button>
    <button class="btn-close" onclick="window.close()">✕ Close</button>
  </div>
  <!-- COVER PAGE -->
  <div class="cover-page">
    <div style="width:100%;height:8pt;background:#2c1810;flex-shrink:0"></div>
    <div style="width:100%;height:3pt;background:#f4a261;flex-shrink:0"></div>
    <div style="flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:4mm 18mm 0;text-align:center;overflow:hidden">
      <div style="margin-bottom:5mm">
        <div style="font-size:9pt;font-weight:bold;text-transform:uppercase;letter-spacing:5px;color:#bbb;margin-bottom:2mm">Property</div>
        <div style="font-size:34pt;font-weight:bold;text-transform:uppercase;letter-spacing:6px;color:#2c1810;line-height:1.05">VALUATION</div>
        <div style="font-size:34pt;font-weight:bold;text-transform:uppercase;letter-spacing:6px;color:#2c1810;line-height:1.05">REPORT</div>
        <div style="font-size:16pt;font-weight:bold;color:#f4a261;letter-spacing:4px;margin-top:2mm">${new Date().getFullYear()}</div>
        <div style="display:flex;align-items:center;justify-content:center;margin:3mm auto 0;width:60mm">
          <div style="flex:1;height:1.5pt;background:#2c1810"></div>
          <div style="width:7pt;height:7pt;background:#f4a261;margin:0 3pt;transform:rotate(45deg);flex-shrink:0"></div>
          <div style="flex:1;height:1.5pt;background:#2c1810"></div>
        </div>
      </div>
      <div style="width:100%;display:flex;flex-direction:column;gap:2.5mm">
        <div style="border:0.75pt solid #e8e0d8;border-left:3pt solid #2c1810;padding:3pt 10pt;background:linear-gradient(to right,#faf6f3,#fff);text-align:left">
          <div style="font-size:8pt;font-weight:bold;text-transform:uppercase;letter-spacing:1.5px;color:#f4a261;margin-bottom:2pt">Client and Address</div>
          ${(s.clients||[]).map(cl=>{
            const rows=[];
            if(cl.showPerson&&cl.person?.name){
              rows.push(`<div style="font-weight:bold;font-size:12pt;color:#2c1810;line-height:1.25">${esc(cl.person.name)}</div>`);
              if(cl.person.contact) rows.push(`<div style="font-size:9.5pt;color:#666">Tel. ${esc(cl.person.contact)}</div>`);
            }
            if(cl.showCompany&&cl.company?.name){
              rows.push(`<div style="font-weight:bold;font-size:12pt;color:#2c1810;line-height:1.25">${esc(cl.company.name)}</div>`);
            }
            return rows.join("");
          }).join('<div style="border-top:0.5pt dashed #ddd;margin:2pt 0"></div>')||`<div style="color:#aaa;font-style:italic">—</div>`}
        </div>
        <div style="border:0.75pt solid #e8e0d8;border-left:3pt solid #2c1810;padding:3pt 10pt;background:linear-gradient(to right,#faf6f3,#fff);text-align:left">
          <div style="font-size:8pt;font-weight:bold;text-transform:uppercase;letter-spacing:1.5px;color:#f4a261;margin-bottom:2pt">Owners and Location of Property</div>
          ${(s.owners||[]).map(ow=>{
            const rows=[];
            if(ow.showPerson&&ow.person?.name) rows.push(`<div style="font-weight:bold;font-size:12pt;color:#2c1810;line-height:1.25">${esc(ow.person.name)}</div>`);
            if(ow.showCompany&&ow.company?.name) rows.push(`<div style="font-weight:bold;font-size:12pt;color:#2c1810;line-height:1.25">${esc(ow.company.name)}</div>`);
            return rows.join("");
          }).join('<div style="border-top:0.5pt dashed #ddd;margin:2pt 0"></div>')||`<div style="color:#aaa;font-style:italic">—</div>`}
          ${(()=>{
            const props=s.properties||[];
            if(!props.length) return '';
            const plotParts=props.map(p=>{const parts=[];if(p.plotNo)parts.push(`Plot No.: <strong>${esc(p.plotNo)}</strong>`);if(p.traceSheetNo)parts.push(`Trace No.: <strong>${esc(p.traceSheetNo)}</strong>`);return parts.join(' &nbsp;|&nbsp; ');}).filter(Boolean);
            if(!plotParts.length) return '';
            return `<div style="font-size:9.5pt;color:#555;margin-top:3pt;border-top:0.5pt solid #eee;padding-top:3pt">${plotParts.join('<br>')}</div>`;
          })()}
          ${(()=>{
            const props=s.properties||[];
            if(!props.length) return '';
            function distM(a,b,c,d){const R=6371000,r=Math.PI/180,dL=(c-a)*r,dN=(d-b)*r,x=Math.sin(dL/2)**2+Math.cos(a*r)*Math.cos(c*r)*Math.sin(dN/2)**2;return R*2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x));}
            const groups=[];
            props.forEach(p=>{
              const addr=(p.addressLalpurja||'').trim();if(!addr)return;
              const lat=parseFloat(p.lat),lng=parseFloat(p.lng),hasGPS=!isNaN(lat)&&!isNaN(lng);
              let merged=false;
              for(const g of groups){const gl=parseFloat(g.lat),gn=parseFloat(g.lng),gh=!isNaN(gl)&&!isNaN(gn);if(g.addr===addr||(hasGPS&&gh&&distM(lat,lng,gl,gn)<=30)){g.plots.push(p.plotNo||'—');merged=true;break;}}
              if(!merged)groups.push({addr,plots:[p.plotNo||'—'],lat:p.lat,lng:p.lng});
            });
            return groups.map(g=>{
              const pl=groups.length>1||g.plots.length>1?` <span style="font-size:8.5pt;color:#888">(Plot: ${g.plots.map(esc).join(', ')})</span>`:'';
              return `<div style="font-size:10pt;color:#555;margin-top:2pt">${esc(g.addr)}${pl}</div>`;
            }).join('');
          })()}
        </div>
        <div style="border:0.75pt solid #e8e0d8;border-left:3pt solid #2c1810;padding:3pt 10pt;background:linear-gradient(to right,#faf6f3,#fff);text-align:left">
          <div style="font-size:8pt;font-weight:bold;text-transform:uppercase;letter-spacing:1.5px;color:#f4a261;margin-bottom:2pt">Present Location of Property</div>
          ${(()=>{
            const props=s.properties||[];
            if(!props.length) return '<div style="color:#aaa;font-style:italic">—</div>';
            function distM(a,b,c,d){const R=6371000,r=Math.PI/180,dL=(c-a)*r,dN=(d-b)*r,x=Math.sin(dL/2)**2+Math.cos(a*r)*Math.cos(c*r)*Math.sin(dN/2)**2;return R*2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x));}
            const groups=[];
            props.forEach(p=>{
              const addr=(p.presentAddress||'').trim();if(!addr)return;
              const lat=parseFloat(p.lat),lng=parseFloat(p.lng),hasGPS=!isNaN(lat)&&!isNaN(lng);
              let merged=false;
              for(const g of groups){const gl=parseFloat(g.lat),gn=parseFloat(g.lng),gh=!isNaN(gl)&&!isNaN(gn);if(g.addr===addr||(hasGPS&&gh&&distM(lat,lng,gl,gn)<=30)){g.plots.push(p.plotNo||'—');merged=true;break;}}
              if(!merged)groups.push({addr,plots:[p.plotNo||'—'],lat:p.lat,lng:p.lng});
            });
            if(!groups.length) return '<div style="color:#aaa;font-style:italic">—</div>';
            return groups.map(g=>{
              const pl=groups.length>1||g.plots.length>1?` <span style="font-size:8.5pt;color:#888">(Plot: ${g.plots.map(esc).join(', ')})</span>`:'';
              return `<div style="font-weight:bold;font-size:12pt;color:#2c1810;line-height:1.25">${esc(g.addr)}${pl}</div>`;
            }).join('');
          })()}
        </div>
        <div style="display:flex;align-items:center;width:100%;margin:0.5mm 0">
          <div style="flex:1;height:0.75pt;background:#e0d8d0"></div>
          <div style="display:flex;gap:3pt;margin:0 5pt">
            <div style="width:4pt;height:4pt;background:#2c1810;transform:rotate(45deg)"></div>
            <div style="width:4pt;height:4pt;background:#f4a261;transform:rotate(45deg)"></div>
            <div style="width:4pt;height:4pt;background:#2c1810;transform:rotate(45deg)"></div>
          </div>
          <div style="flex:1;height:0.75pt;background:#e0d8d0"></div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:2.5mm">
          <div style="border:0.75pt solid #f4e8d8;border-left:3pt solid #f4a261;padding:3pt 10pt;background:linear-gradient(to right,#fffbf6,#fff);text-align:left">
            <div style="font-size:8pt;font-weight:bold;text-transform:uppercase;letter-spacing:1.5px;color:#2c1810;margin-bottom:2pt">Submitted To</div>
            <div style="font-weight:bold;font-size:12pt;color:#2c1810;line-height:1.25">${esc(s.bank)}</div>
            ${s.branch?`<div style="font-size:10pt;color:#444">${esc(s.branch)} Branch</div>`:""}
          </div>
          <div style="border:0.75pt solid #f4e8d8;border-left:3pt solid #f4a261;padding:3pt 10pt;background:linear-gradient(to right,#fffbf6,#fff);text-align:left">
            <div style="font-size:8pt;font-weight:bold;text-transform:uppercase;letter-spacing:1.5px;color:#2c1810;margin-bottom:2pt">Submitted By</div>
            <div style="font-weight:bold;font-size:12pt;color:#2c1810;line-height:1.25">${esc(vi.company, "Neo-Civic Consulting (P). Ltd")}</div>
            ${vi.phone?`<div style="font-size:9.5pt;color:#555">Tel. ${esc(vi.phone)}</div>`:""}
          </div>
        </div>
      </div>
    </div>
    <div style="width:100%;height:3pt;background:#f4a261;flex-shrink:0;margin-top:4mm"></div>
    <div style="width:100%;height:8pt;background:#2c1810;flex-shrink:0"></div>
  </div>
  <!-- COVER LETTER -->
  <div class="letter-page">
    ${lhSrc ? `<div class="letter-page-bg">
      <img src="${lhSrc}" alt="Company Letterhead"/>
    </div>` : ""}
    ${wmHtml}
    <div style="${contentStyle}">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:3pt;font-size:9.5pt">
      <div><strong>Ref. No.: Neo-${String(Math.floor(Math.random()*900)+100).padStart(3,'0')}-${String(Math.floor(Math.random()*900)+100)}</strong></div>
      <div>Date:-&nbsp;&nbsp;<strong>${s.reportDate||new Date().toLocaleDateString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}</strong></div>
    </div>
    <div style="margin-bottom:3pt;font-size:9.5pt;line-height:1.45">
      To<br/>
      The Manager<br/>
      <strong>${esc(s.bank, "[Bank Name]")}</strong><br/>
      ${s.branch?`<strong>${esc(s.branch)} Branch</strong>`:""}
    </div>
    <div style="text-align:center;font-weight:bold;text-decoration:underline;font-size:10pt;margin:3pt 0 4pt">
      Subject: Assessment of Property Values
    </div>
    <div style="font-size:9.5pt;line-height:1.38;text-align:justify">
      <p style="margin-bottom:2pt">Dear Sir/ Madam,</p>
      <p style="margin-bottom:3pt">&nbsp;&nbsp;&nbsp;We are pleased to submit herewith Valuation Report of following property based on present market value, which is intended to mortgage in favor of ${esc(s.bank, "[Bank Name]")} by:</p>
      ${(()=>{
        const cl0=s.clients||[],pr0=s.properties||[];
        const multiCl=cl0.length>1;
        const td=(v,w,bg,bold)=>`<td style="padding:2pt 6pt;border:0.75pt solid #bbb${w?';width:'+w:''}${bg?';background:'+bg:''}${bold?';font-weight:bold':''}">${v}</td>`;
        let r=`<table style="width:100%;border-collapse:collapse;margin-bottom:3pt;font-size:9.5pt">`;
        if(!multiCl){
          const cl=cl0[0]||{};
          const nm=esc(cl.showPerson&&cl.person?.name?cl.person.name:cl.showCompany&&cl.company?.name?cl.company.name:'—');
          const addr=esc(cl.person?.address||cl.company?.address||pr0[0]?.presentAddress||'—');
          const phone=esc(cl.person?.contact||cl.company?.contact||'—');
          r+=`<tr>${td('Name','34%','','bold')}${td(':','3%')}${td(nm)}</tr>`;
          r+=`<tr>${td('Address of the Client','34%')}${td(':','3%')}${td(addr)}</tr>`;
          r+=`<tr>${td('Contact No.','34%')}${td(':','3%')}${td(phone)}</tr>`;
        } else {
          r+=`<tr>${td('No.','4%','#f5f0eb','bold')}${td('Name','28%','#f5f0eb','bold')}${td('Address','','#f5f0eb','bold')}${td('Contact No.','16%','#f5f0eb','bold')}</tr>`;
          cl0.forEach((cl,i)=>{
            const nm=esc(cl.showPerson&&cl.person?.name?cl.person.name:cl.showCompany&&cl.company?.name?cl.company.name:'—');
            const addr=esc(cl.person?.address||cl.company?.address||'—');
            const phone=esc(cl.person?.contact||cl.company?.contact||'—');
            r+=`<tr>${td(i+1,'4%')}${td(nm,'28%')}${td(addr)}${td(phone,'16%')}</tr>`;
          });
        }
        r+=`</table>`;
        return r;
      })()}
      <p style="margin-bottom:3pt">We have taken all care to ascertain value of properties as directed by the client:</p>
      ${(()=>{
        const pr0=s.properties||[];
        const ow0=s.owners||[];
        const multiP=pr0.length>1;
        const multiO=ow0.length>1;
        const aFmt=p=>{if(!p)return'—';if(p.areaUnit==='radp'){const r=p.areaRadp||{};return[r.r&&r.r+'-R',r.a&&r.a+'-A',r.p&&r.p+'-P',r.d&&r.d+'-D'].filter(Boolean).join(' ')||'—';}return p.areaSqm?p.areaSqm+' sq.m':'—';};
        const distM=(a,b,c,d)=>{const R=6371000,rad=Math.PI/180,dL=(c-a)*rad,dN=(d-b)*rad,x=Math.sin(dL/2)**2+Math.cos(a*rad)*Math.cos(c*rad)*Math.sin(dN/2)**2;return R*2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x));};
        const dedupAddr=(key)=>{const groups=[];pr0.forEach(p=>{const addr=(p[key]||'').trim();if(!addr)return;const lat=parseFloat(p.lat),lng=parseFloat(p.lng),hasGPS=!isNaN(lat)&&!isNaN(lng);let merged=false;for(const g of groups){const gl=parseFloat(g.lat),gn=parseFloat(g.lng),gh=!isNaN(gl)&&!isNaN(gn);if(g.addr===addr||(hasGPS&&gh&&distM(lat,lng,gl,gn)<=30)){g.plots.push(p.plotNo||'—');merged=true;break;}}if(!merged)groups.push({addr,plots:[p.plotNo||'—'],lat:p.lat,lng:p.lng});});return groups;};
        if(!multiP && !multiO){
          const p=pr0[0]||{};
          const ow=ow0[0]||{};
          const owName=esc(ow.showPerson&&ow.person?.name?ow.person.name:ow.showCompany&&ow.company?.name?ow.company.name:p.ownerName||'—');
          const td=(v,w)=>`<td style="padding:2pt 5pt;border:0.75pt solid #bbb${w?';width:'+w:''}">${v}</td>`;
          const locVal = esc([p.addressLalpurja, p.location].filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i).join(', ') || '—');
          const preVal = esc(p.presentAddress || '—');
          return `<table style="width:100%;border-collapse:collapse;margin-bottom:3pt;font-size:9.5pt">
            <tr>${td('Name of Owner','34%')}${td(':','3%')}${td(owName)}</tr>
            <tr>${td('Location of Property')}${td(':','3%')}${td(locVal)}</tr>
            <tr>${td('Present Location')}${td(':','3%')}${td(preVal)}</tr>
            <tr>${td('Plot No.')}${td(':','3%')}${td(esc(p.plotNo))}</tr>
            <tr>${td('Area')}${td(':','3%')}${td(aFmt(p))}</tr>
            <tr>${td('Sheet No. of Blue Print')}${td(':','3%')}${td(esc(p.traceSheetNo))}</tr>
          </table>`;
        }
        const th=v=>`<th style="background:#2c1810;color:#fff;padding:2pt 5pt;text-align:left;font-size:9pt">${v}</th>`;
        const td2=v=>`<td style="padding:2pt 5pt;border:0.5pt solid #ddd;font-size:9.5pt">${v}</td>`;
        let r=`<div style="font-weight:bold;font-size:8.5pt;color:#2c1810;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:2pt">Plot / Property Details</div>`;
        r+=`<table style="width:100%;border-collapse:collapse;margin-bottom:3pt">`;
        r+=`<thead><tr>`;
        if(multiO) r+=th('Owner');
        r+=th('Plot No.')+th('Area')+th('Trace Sheet No.')+`</tr></thead><tbody>`;
        pr0.forEach((p,pi)=>{
          const ow=ow0[pi]||ow0[0]||{};
          const owName=esc(ow.showPerson&&ow.person?.name?ow.person.name:ow.showCompany&&ow.company?.name?ow.company.name:p.ownerName||'—');
          r+=`<tr>`;
          if(multiO) r+=td2(owName);
          r+=td2(esc(p.plotNo))+td2(aFmt(p))+td2(esc(p.traceSheetNo))+`</tr>`;
        });
        r+=`</tbody></table>`;
        const dedupAddrCombined = () => {
          const groups = [];
          pr0.forEach(p => {
            const addr = [p.addressLalpurja, p.location].filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i).join(', ').trim();
            if(!addr) return;
            const lat=parseFloat(p.lat),lng=parseFloat(p.lng),hasGPS=!isNaN(lat)&&!isNaN(lng);
            let merged=false;
            for(const g of groups){
              const gl=parseFloat(g.lat),gn=parseFloat(g.lng),gh=!isNaN(gl)&&!isNaN(gn);
              if(g.addr===addr||(hasGPS&&gh&&distM(lat,lng,gl,gn)<=30)){g.plots.push(p.plotNo||'—');merged=true;break;}
            }
            if(!merged) groups.push({addr,plots:[p.plotNo||'—'],lat:p.lat,lng:p.lng});
          });
          return groups;
        };
        const locGroups=dedupAddrCombined();
        const preGroups=dedupAddr('presentAddress');
        const td3=(v,w)=>`<td style="padding:2pt 5pt;border:0.75pt solid #bbb${w?';width:'+w:''}">${v}</td>`;
        r+=`<table style="width:100%;border-collapse:collapse;margin-bottom:3pt;font-size:9.5pt">`;
        if(locGroups.length>=1){
          const locStr=locGroups.map(g=>esc(g.addr)+(locGroups.length>1||g.plots.length>1?` (Plot: ${g.plots.map(esc).join(', ')})`:'') ).join('<br>');
          r+=`<tr>${td3('Location of Property','34%')}${td3(':','3%')}${td3(locStr||'—')}</tr>`;
        }
        if(preGroups.length>=1){
          const preStr=preGroups.map(g=>esc(g.addr)+(preGroups.length>1||g.plots.length>1?` (Plot: ${g.plots.map(esc).join(', ')})`:'') ).join('<br>');
          r+=`<tr>${td3('Present Location','34%')}${td3(':','3%')}${td3(preStr||'—')}</tr>`;
        }
        r+=`</table>`;
        return r;
      })()}
      <p style="margin-bottom:2pt"><u>We hereby declare and certify that:</u></p>
      <p style="margin-bottom:2pt">a) We have no direct and indirect interest in said properties.</p>
      <p style="margin-bottom:2pt">b) The information furnished is true and correct to the best of our knowledge and belief, which are based on documents furnished by the client.</p>
      <p style="margin-bottom:2pt">c) We have prepared independent valuation assessments for each category of assets.</p>
      <p style="margin-bottom:2pt">The current value of the property assessed comes to</p>
      <table style="width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:2pt">
        <tr>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;width:40%;font-weight:bold">Net Commercial value</td>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;width:8%;text-align:center">NRs.</td>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;font-weight:bold;text-align:center;font-size:10pt">${finalComm.toLocaleString("en-NP")}</td>
        </tr>
        <tr>
          <td colspan="3" style="padding:1pt 6pt;border:0.75pt solid #ddd;font-style:italic;font-size:9pt;color:#444">In Word: ${toWords(finalComm)} Rupees only .</td>
        </tr>
        <tr>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;font-weight:bold">Net Fair Market value</td>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;text-align:center">NRs.</td>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;font-weight:bold;text-align:center;font-size:10pt">${finalFMV.toLocaleString("en-NP")}</td>
        </tr>
        <tr>
          <td colspan="3" style="padding:1pt 6pt;border:0.75pt solid #ddd;font-style:italic;font-size:9pt;color:#444">In Word: ${toWords(finalFMV)} Rupees only .</td>
        </tr>
        <tr>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;font-weight:bold">Net Distress Value</td>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;text-align:center">NRs.</td>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;font-weight:bold;text-align:center;font-size:10pt">${distress.toLocaleString("en-NP")}</td>
        </tr>
        <tr>
          <td colspan="3" style="padding:1pt 6pt;border:0.75pt solid #ddd;font-style:italic;font-size:9pt;color:#444">In Word: ${toWords(distress)} Rupees only .</td>
        </tr>
        ${finalGovFinal>0?`<tr>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;font-weight:bold">Government Value of Land</td>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;text-align:center">NRs.</td>
          <td style="padding:3pt 6pt;border:0.75pt solid #ddd;font-weight:bold;text-align:center;font-size:10pt">${finalGovFinal.toLocaleString("en-NP")}</td>
        </tr>
        <tr>
          <td colspan="3" style="padding:1pt 6pt;border:0.75pt solid #ddd;font-style:italic;font-size:9pt;color:#444">In Word: ${toWords(finalGovFinal)} Rupees only .</td>
        </tr>`:""}
      </table>
      <p style="margin-top:4pt;margin-bottom:2pt">we hope you will find this in order. Thanking you for entrusting us with our works and assuring you of our best professional service.</p>
      <p style="margin-bottom:0">Sincerely yours;</p>
    </div>
    <div style="margin-top:4pt">
      <div style="border-top:1pt solid #000;width:45mm;margin-top:14pt;margin-bottom:2pt"></div>
      <div style="font-size:10pt">For ${esc(vi.company, "Neo- Civic Consulting (P). Ltd")}</div>
      <div style="font-weight:bold;font-size:10pt">${esc(vi.name, "Er. Saakar Rimal")}</div>
      <div style="font-size:10pt">Contact No.: ${esc(vi.phone||vi.licenseNo, "014353196")}</div>
    </div>
    </div>
  </div>

  <!-- TABLE OF CONTENTS -->
  <div class="toc-page">
    <div class="toc-title">Table of Contents</div>
    <table class="toc-table">
      <thead><tr><th style="width:10%">No.</th><th>Section Title</th><th style="width:12%;text-align:right">Page</th></tr></thead>
      <tbody>${tocRows}</tbody>
    </table>
    <div style="margin-top:10mm;font-size:10pt;color:#666;font-style:italic;text-align:center">
      ${esc(suggestedFilename, "Final Property Valuation Report")} &nbsp;|&nbsp; ${esc(s.reportDate, "")}
    </div>
  </div>
  <!-- MAIN REPORT BODY -->
  <div class="page-wrap">
    <div style="border:2pt solid #2c1810;padding:10pt 14pt;margin-bottom:16pt;background:#fff9f6;text-align:center">
      <div style="font-size:9.5pt;font-weight:bold;text-transform:uppercase;letter-spacing:2px;color:#888;margin-bottom:3pt">${esc(s.bank, "Bank Name")}${s.branch?" &nbsp;|&nbsp; "+esc(s.branch):""}</div>
      <div style="font-size:17pt;font-weight:bold;text-transform:uppercase;letter-spacing:1.5px;color:#2c1810;margin:2pt 0">Property Valuation Report</div>
      <div style="width:60mm;height:2pt;background:#f4a261;margin:5pt auto"></div>
      <div style="font-size:11pt;color:#555;margin-top:4pt">Security Assessment for Loan / Mortgage &nbsp;|&nbsp; <strong style="color:#2c1810;letter-spacing:1px">FINAL REPORT</strong></div>
      <div style="font-size:10pt;color:#666;margin-top:6pt;display:flex;justify-content:center;gap:20pt;flex-wrap:wrap">
        <span>Visit: <strong>${esc(s.visitDate)}</strong></span>
        <span>Report: <strong>${esc(s.reportDate)}</strong></span>
        <span>Valuator: <strong>${esc(vi.name)}</strong></span>
      </div>
    </div>

    <div class="ch-head" id="ch-1">Chapter 1 &nbsp;|&nbsp; General Information</div>

    <div class="sec-h3">1.1 &nbsp; Bank &amp; Branch</div>
    <table class="info-table">
      <tr><td>Bank</td><td>${esc(s.bank)}</td><td>Branch</td><td>${esc(s.branch)}</td></tr>
      <tr><td>Date of Field Visit</td><td>${esc(s.visitDate)}</td><td>Date of Reporting</td><td>${esc(s.reportDate)}</td></tr>
    </table>

    <div class="sec-h3">1.2 &nbsp; Client Information</div>
    ${clientsHTML||`<p style="font-style:italic;color:#888">No client information entered.</p>`}

    <div class="sec-h3">1.3 &nbsp; Owner Information</div>
    ${ownersHTML||`<p style="font-style:italic;color:#888">No owner information entered.</p>`}

    <div class="sec-h3">1.4 &nbsp; Property Register Details</div>
    ${(s.properties||[]).length===0 ? `<p style="font-style:italic;color:#888;padding:10pt">No property data entered.</p>` :
      (s.properties||[]).map((p,pi)=>{
        const sqmVal2=(()=>{if(p.areaUnit==='sqm')return parseFloat(p.areaSqm)||0;const r=p.areaRadp||{};return(parseFloat(r.r)||0)*508.72+(parseFloat(r.a)||0)*31.795+(parseFloat(r.p)||0)*7.949+(parseFloat(r.d)||0)*1.987;})();
        const lalpurjaArea=(()=>{if(p.areaUnit==='radp'){const r=p.areaRadp||{};const parts=[];if(r.r)parts.push(r.r+' Ropani');if(r.a)parts.push(r.a+' Aana');if(r.p)parts.push(r.p+' Paisa');if(r.d)parts.push(r.d+' Dam');return parts.join(' ')||'—';}return p.areaSqm?p.areaSqm+' sq.m':'—';})();
        const radpStr=p.areaUnit==='radp'?`${p.areaRadp?.r||0}-${p.areaRadp?.a||0}-${p.areaRadp?.p||0}-${p.areaRadp?.d||0}`:`${Math.floor(sqmVal2/508.72)}-${Math.floor((sqmVal2%508.72)/31.795)}-${Math.floor((sqmVal2%31.795)/7.949)}-${Math.floor((sqmVal2%7.949)/1.987)}`;
        const sr2=(label,val)=>`<tr><td style="background:#f5f0eb;font-weight:bold;font-size:9.5pt;width:36%;color:#2c1810;padding:5pt 8pt;border:0.5pt solid #ccc">${label}</td><td style="font-size:10pt;padding:5pt 8pt;border:0.5pt solid #ccc">${val||'—'}</td></tr>`;
        return `<div style="margin-bottom:14pt;break-inside:avoid"><div style="background:#2c1810;color:#fff;padding:5pt 10pt;font-weight:bold;font-size:10.5pt">Property ${pi+1} &nbsp;|&nbsp; Plot No. ${esc(p.plotNo)} &nbsp;|&nbsp; Trace Sheet No. ${esc(p.traceSheetNo)}</div><table style="width:100%;border-collapse:collapse">${sr2('Plot No. (Kitta)',esc(p.plotNo))}${sr2('Trace Sheet No.',esc(p.traceSheetNo))}${sr2('Name of Owner',esc(p.ownerName))}${sr2('Type of Land',esc(p.landType))}${sr2('Category of Land',esc(p.category))}${sr2('Type of Ownership',esc(p.ownershipType))}${sr2('Area as per Lalpurja',lalpurjaArea)}${sr2('Area (sq.m)',sqmVal2>0?sqmVal2.toFixed(2)+' sq.m':'—')}${sr2('Area (R-A-P-D)',radpStr)}${sr2('Address as per Lalpurja',esc(p.addressLalpurja))}${sr2('Present Address',esc(p.presentAddress))}${sr2('Google Plus Code',esc(p.googlePlusCode))}${sr2('Latitude / Longitude',(p.lat&&p.lng)?p.lat+' / '+p.lng:'—')}</table></div>`;
      }).join('')
    }

    <div class="ch-head page-break" id="ch-2">Chapter 2 &nbsp;|&nbsp; Property Description &amp; Physical Characteristics</div>

    <div class="sec-h3">2.1 &nbsp; Physical Description</div>
    ${propDescHTML}

    <div class="sec-h3" id="toc-s3">2.2 &nbsp; Location Maps</div>
    ${mapsHTML}

    <div class="sec-h3" id="toc-s5">2.3 &nbsp; Access to Property</div>
    <table>
      <thead><tr><th>Plot No.</th><th>Type of Road</th><th>Frontage (ft)</th><th>Width (Field) ft</th><th>Width (Trace) ft</th><th>Remarks</th></tr></thead>
      <tbody>${roadAccessHTML||`<tr><td colspan="6" style="text-align:center;font-style:italic;color:#888">No road access data</td></tr>`}</tbody>
    </table>

    <div class="sec-h3">2.4 &nbsp; Boundary Declaration</div>
    <table>
      <thead><tr><th>Plot No.</th><th>East</th><th>West</th><th>North</th><th>South</th><th>Remarks</th></tr></thead>
      <tbody>${boundaryRows||`<tr><td colspan="6" style="text-align:center;font-style:italic;color:#888">No boundary data</td></tr>`}</tbody>
    </table>

    <div class="ch-head page-break" id="ch-3">Chapter 3 &nbsp;|&nbsp; Area to be Mortgaged &amp; Considered Area</div>

    <div class="sec-h3">3.1 &nbsp; Area to be Mortgaged</div>
    <table>
      <thead><tr><th>Plot No.</th><th>Trace Sheet No.</th><th>Owner Name</th><th>Area (Lalpurja)</th></tr></thead>
      <tbody>${mortProps.map(p=>`<tr><td>${esc(p.plotNo)}</td><td>${esc(p.traceSheetNo)}</td><td>${esc(p.ownerName)}</td><td>${areaDisplay(p)}</td></tr>`).join("")||`<tr><td colspan="4" style="text-align:center;font-style:italic;color:#888">No mortgaged properties selected</td></tr>`}</tbody>
    </table>

    <div class="sec-h3">3.2 &nbsp; Deduction Declaration</div>
    <table>
      <thead><tr><th>Plot No.</th><th>Dimension</th><th>Deduction Area (sq.m)</th><th>Reason / Remarks</th></tr></thead>
      <tbody>${deductRows||`<tr><td colspan="4" style="text-align:center;font-style:italic;color:#888">No deductions</td></tr>`}</tbody>
    </table>

    <div class="sec-h3">3.3 &nbsp; Considered Area for Valuation</div>
    <table>
      <thead><tr><th>Plot No.</th><th>Lalpurja (sq.m)</th><th>Measured (sq.m)</th><th>Deduction (sq.m)</th><th>Considered (sq.m)</th><th>R-A-P-D</th><th>Aana</th></tr></thead>
      <tbody>${consideredRows}${consideredTotalRow}</tbody>
    </table>

    <div class="ch-head page-break" id="ch-4">Chapter 4 &nbsp;|&nbsp; Building Details</div>
    ${s.hasBuilding===false
      ? `<p style="font-style:italic;color:#888;padding:10pt">No building available on this property.</p>`
      : s.hasBuilding==="skip"
      ? `<p style="font-style:italic;color:#e67e22;padding:10pt">Building exists but valuation of building has not been carried out.</p>`
      : (s.buildings||[]).length
        ? `<div class="sec-h3">4.1 &nbsp; Building Area &amp; General Details</div>${buildingsDetailHTML}`
        : `<p style="font-style:italic;color:#888;padding:10pt">No building data entered.</p>`}

    ${(s.buildings||[]).length && s.hasBuilding===true ? `
    <div class="sec-h3">4.2 &nbsp; Technical Specifications</div>
    ${(s.buildings||[]).map((b,bi)=>{
      const bd = s.buildingDetails||{};
      const det = bd[b.id]||{};
      const floorList = b.areaTable||[];
      const totalH = det.totalHeight || floorList.reduce((sum,r)=>sum+(parseFloat((det.floorHeights||{})[r.id])||0),0);
      const fhRows = floorList.map(r=>`<tr><td>${esc(r.description)}</td><td>${(det.floorHeights||{})[r.id]||"—"} ft</td><td>${esc((det.floorHeightRemarks||{})[r.id], "")}</td></tr>`).join("");
      const sr = (lbl, val) => `<tr><td style="background:#f5f0eb;font-weight:bold;width:42%;font-size:9.5pt;text-transform:uppercase;color:#2c1810">${lbl}</td><td>${esc(val)}</td></tr>`;
      const gh = (title) => `<tr><td colspan="2" style="background:#2c1810;color:#fff;font-weight:bold;text-transform:uppercase;font-size:9.5pt;padding:4pt 7pt">${title}</td></tr>`;
      return `<p class="sub-head">Building ${bi+1}${b.plotNo?" — Plot No. "+esc(b.plotNo):""}</p>
      ${floorList.length?`<p style="font-weight:bold;margin:5pt 0 3pt">Floor Heights:</p>
      <table><thead><tr><th>Floor</th><th>Height (ft)</th><th>Remarks</th></tr></thead>
      <tbody>${fhRows}<tr class="total-row"><td><strong>Total Height</strong></td><td><strong>${totalH||"—"} ft</strong></td><td></td></tr></tbody></table>`:""}
      <table class="info-table" style="margin-top:6pt">
        ${gh("Structural Members")}${sr("Min. Column Size",det.minColumn)}${sr("Min. Beam Size",det.minBeam)}${sr("DPC / Tie Beam",det.dpcTieBeam)}${sr("Slab Thickness",det.slabThickness)}
        ${gh("Walls, Doors &amp; Windows")}${sr("External Wall",det.externalWall)}${sr("Internal Wall",det.internalWall)}${sr("Door Material",det.doorMaterial)}${sr("Window Material",det.windowMaterial)}${sr("Staircase",det.staircase)}${sr("Roof",det.roof)}
        ${gh("Finishing &amp; Fixtures")}${sr("External Finishing",det.externalFinishing)}${sr("Internal Finishing",det.internalFinishing)}${sr("Flooring",det.flooring)}${sr("Verandah",det.verandah)}${sr("Kitchen / Dining",det.kitchen)}${sr("Bathroom / Toilet",det.bathroom)}
        ${gh("Services &amp; Utilities")}${sr("Sanitary &amp; Plumbing",det.sanitary)}${sr("Electricity System",det.electricitySystem)}${sr("Telephone",det.telephone)}${sr("UG Water Tank",det.ugWaterTank)}${sr("OH Water Tank",det.ohWaterTank)}${sr("Solar Panel",det.solarPanel)}${sr("Water Supply",det.waterSupply)}${sr("Deep Boring",det.deepBoring)}
        ${gh("Condition &amp; Remarks")}${sr("Defects",det.defects)}${sr("Repair &amp; Maintenance",det.repairMaintenance)}${sr("Comments",det.comments)}
      </table>`;
    }).join('<div class="dashed-sep"></div>')}` : ""}
    <div class="ch-head page-break" id="ch-5">Chapter 5 &nbsp;|&nbsp; Valuation of Property</div>

    <div class="sec-h3" id="toc-s7">5.1 &nbsp; Valuation of Land</div>
    <table>
      <thead><tr><th>Plot No. / Zone</th><th>Area (sq.m)</th><th>Area (Aana)</th><th>Commercial Rate (NPR/Aana)</th><th>FMV Rate (NPR/Aana)</th><th>Commercial Value (NPR)</th><th>FMV Value (NPR)</th></tr></thead>
      <tbody>${landValRows||`<tr><td colspan="7" style="text-align:center;font-style:italic;color:#888">No data</td></tr>`}
        <tr class="total-row"><td colspan="5"><strong>TOTAL LAND VALUE (Rounded)</strong></td>
          <td><strong>NPR ${totalLandComm.toLocaleString("en-NP")}</strong></td>
          <td><strong>NPR ${totalLandFMV2.toLocaleString("en-NP")}</strong></td></tr>
      </tbody>
    </table>

    ${(s.buildings||[]).length && s.hasBuilding===true ? `
    <div class="sec-h3" id="toc-s8">5.2 &nbsp; Valuation of Building</div>
    <table class="info-table">
      ${(s.buildings||[]).map((b,i)=>{
        const v=bv[b.id]||{};
        const floorCalcs=(b.areaTable||[]).map(row=>({area:parseFloat(row.areaActual)||0,rate:parseFloat((v.floorRates||{})[row.id])||0}));
        const baseCost=floorCalcs.reduce((s,f)=>s+f.area*f.rate,0);
        const sanCost=baseCost*(parseFloat(v.sanitaryPct)||0)/100;
        const elecCost=baseCost*(parseFloat(v.electricalPct)||0)/100;
        const finCost=baseCost*(parseFloat(v.finishingPct)||0)/100;
        const totalWithFix=baseCost+sanCost+elecCost+finCost;
        const age=parseFloat(b.ageOfBuilding)||0,depRate=parseFloat(v.depreciationRate)||2.25;
        const totalDepPct=Math.min(100,age*depRate),totalDep=totalWithFix*totalDepPct/100;
        const actual=Math.max(0,totalWithFix-totalDep),rounded=Math.floor(actual/100)*100;
        return `<tr><td>Building ${i+1}${b.plotNo?" — Plot No. "+esc(b.plotNo):""}</td><td>NPR ${rounded.toLocaleString("en-NP")}</td><td>Depreciation ${totalDepPct.toFixed(1)}%</td><td>${age} yrs — ${depRate}%/yr</td></tr>`;
      }).join("")}
      <tr class="total-row"><td colspan="3"><strong>TOTAL BUILDING VALUE</strong></td><td><strong>NPR ${totalBuildingVal.toLocaleString("en-NP")}</strong></td></tr>
    </table>` : ""}

    <div class="ch-head page-break" id="ch-6">Chapter 6 &nbsp;|&nbsp; Summary of Valuation</div>
    <div class="val-summary no-break">
      <div class="val-summary-head">Summary of Property Valuation</div>

      <div style="padding:10pt 14pt 6pt;font-weight:bold;font-size:10.5pt;color:#2c1810;text-transform:uppercase;letter-spacing:0.5px;border-bottom:0.5pt solid #ddd">A. Commercial Value</div>
      <div style="padding:6pt 14pt">
        <table style="margin:0;font-size:11pt">
          <tbody>
            <tr><td style="border:none;padding:3pt 0">1. Land Value</td><td style="border:none;text-align:right;width:45%">NPR ${totalLandComm.toLocaleString("en-NP")}</td></tr>
            ${(s.buildings||[]).length&&s.hasBuilding===true?`<tr><td style="border:none;padding:3pt 0">2. Building Value</td><td style="border:none;text-align:right">NPR ${totalBuildingVal.toLocaleString("en-NP")}</td></tr>`:""}
            <tr style="border-top:1pt solid #2c1810"><td style="border:none;padding:4pt 0;font-weight:bold">Total Commercial Value (A)</td><td style="border:none;text-align:right;font-weight:bold;font-size:13pt;color:#2c1810">NPR ${finalComm.toLocaleString("en-NP")}</td></tr>
          </tbody>
        </table>
        <div class="in-words">Nepalese Rupees ${toWords(finalComm)} Only &nbsp;(NPR ${finalComm.toLocaleString("en-NP")}/-)</div>
      </div>

      <div style="padding:10pt 14pt 6pt;font-weight:bold;font-size:10.5pt;color:#8b4513;text-transform:uppercase;letter-spacing:0.5px;border-bottom:0.5pt solid #ddd;border-top:1pt solid #eee">B. Fair Market Value (FMV)</div>
      <div style="padding:6pt 14pt">
        <table style="margin:0;font-size:11pt">
          <tbody>
            <tr><td style="border:none;padding:3pt 0">1. Land Value</td><td style="border:none;text-align:right;width:45%">NPR ${totalLandFMV2.toLocaleString("en-NP")}</td></tr>
            ${(s.buildings||[]).length&&s.hasBuilding===true?`<tr><td style="border:none;padding:3pt 0">2. Building Value</td><td style="border:none;text-align:right">NPR ${totalBuildingVal.toLocaleString("en-NP")}</td></tr>`:""}
            <tr style="border-top:1pt solid #8b4513"><td style="border:none;padding:4pt 0;font-weight:bold">Total Fair Market Value (B)</td><td style="border:none;text-align:right;font-weight:bold;font-size:13pt;color:#8b4513">NPR ${finalFMV.toLocaleString("en-NP")}</td></tr>
          </tbody>
        </table>
        <div class="in-words" style="border-color:#8b4513">Nepalese Rupees ${toWords(finalFMV)} Only &nbsp;(NPR ${finalFMV.toLocaleString("en-NP")}/-)</div>
      </div>

      <div style="padding:10pt 14pt 6pt;font-weight:bold;font-size:10.5pt;color:#c0392b;text-transform:uppercase;letter-spacing:0.5px;border-bottom:0.5pt solid #ddd;border-top:1pt solid #eee">C. Distress Value (${Math.round(distressMult2*100)}% of FMV)</div>
      <div style="padding:6pt 14pt">
        <table style="margin:0;font-size:11pt">
          <tbody>
            <tr><td style="border:none;padding:3pt 0">${Math.round(distressMult2*100)}% of Fair Market Value</td><td style="border:none;text-align:right;width:45%;font-weight:bold;font-size:13pt;color:#c0392b">NPR ${distress.toLocaleString("en-NP")}</td></tr>
          </tbody>
        </table>
        <div class="in-words" style="border-color:#c0392b">Nepalese Rupees ${toWords(distress)} Only &nbsp;(NPR ${distress.toLocaleString("en-NP")}/-)</div>
      </div>

      ${finalGovFinal>0?`
      <div style="padding:10pt 14pt 6pt;font-weight:bold;font-size:10.5pt;color:#1565c0;text-transform:uppercase;letter-spacing:0.5px;border-bottom:0.5pt solid #ddd;border-top:1pt solid #eee">D. Government Value of Land</div>
      <div style="padding:6pt 14pt">
        <table style="margin:0;font-size:11pt">
          <tbody>
            <tr><td style="border:none;padding:3pt 0">Total Government Value</td><td style="border:none;text-align:right;width:45%;font-weight:bold;font-size:13pt;color:#1565c0">NPR ${finalGovFinal.toLocaleString("en-NP")}</td></tr>
          </tbody>
        </table>
        <div class="in-words" style="border-color:#1565c0;color:#1565c0">Nepalese Rupees ${toWords(finalGovFinal)} Only &nbsp;(NPR ${finalGovFinal.toLocaleString("en-NP")}/-)</div>
      </div>`:""}

      <div style="display:grid;grid-template-columns:${finalGovFinal>0?"1fr 1fr 1fr 1fr":"1fr 1fr 1fr"};gap:0;border-top:2pt solid #2c1810">
        <div class="val-row a" style="flex-direction:column;align-items:flex-start"><div class="vlbl">A. Commercial Value</div><div class="vamt">NPR ${finalComm.toLocaleString("en-NP")}</div></div>
        <div class="val-row b" style="flex-direction:column;align-items:flex-start;border-left:1pt solid #ddd;border-right:1pt solid #ddd"><div class="vlbl">B. Fair Market Value</div><div class="vamt">NPR ${finalFMV.toLocaleString("en-NP")}</div></div>
        <div class="val-row c" style="flex-direction:column;align-items:flex-start"><div class="vlbl">C. Distress Value (${Math.round(distressMult2*100)}%)</div><div class="vamt">NPR ${distress.toLocaleString("en-NP")}</div></div>
        ${finalGovFinal>0?`<div class="val-row" style="flex-direction:column;align-items:flex-start;background:#e8f0fe;border-left:1pt solid #ddd"><div class="vlbl" style="color:#1565c0">D. Government Value</div><div class="vamt" style="color:#1565c0">NPR ${finalGovFinal.toLocaleString("en-NP")}</div></div>`:""}
      </div>
    </div>

    <div class="ch-head" id="ch-7">Chapter 7 &nbsp;|&nbsp; Remarks &amp; Limiting Conditions</div>

    <div class="sec-h3">7.1 &nbsp; Remarks</div>
    <div class="remarks-box" style="padding:6pt 10pt;font-size:10.5pt;line-height:1.35">${esc(s.remarks)}</div>

    <div class="sec-h3">7.2 &nbsp; Limiting Conditions</div>
    <div class="remarks-box" style="padding:6pt 10pt;font-size:10.5pt;line-height:1.35">${esc(s.limitingConditions, "This valuation is valid for six months from the date of reporting and is subject to verification of title documents, physical demarcation of boundary, and prevailing market conditions.")}</div>

    <div class="sec-h3">7.3 &nbsp; Opinion</div>
    <div class="remarks-box" style="padding:6pt 10pt;font-size:10.5pt;line-height:1.35;text-align:justify;margin-bottom:0">
      In our opinion this property may be taken as mortgage for the FMV amount recommended in the valuation certificate, however, all the remarks made above shall be taken into consideration and all legal documents shall be scrutinized by legal expert.
    </div>

    <div class="sec-h3" style="margin-top:4pt">7.4 &nbsp; Declaration</div>
    <div class="remarks-box" style="padding:6pt 10pt;font-size:10.5pt;line-height:1.35;text-align:justify;margin-bottom:0">
      <p style="margin:0">This valuation was conducted for the purpose of establishing Fair Market and Distress Values of the said property for Client and Bank for mortgaging these properties. It is not to be used for any other purpose, and no part of this report is to be disseminated to the public or third parties.
      We certify that our firm is fully authorized to carry out the valuation work under the prevalent laws and we are fully equipped and competent to carry out the assignment and have the necessary qualifications, skills and experience required for the same.
      We also certify that no individual in our firm has any financial interest in the said property.
      To the best of our knowledge, all matters of a factual nature discussed in this report are true and correct. No important factors have been intentionally overlooked or withheld.
      We have physically inspected, verified and measured the properties in the presence of the Client/Representative of the Client.
      We transformed all details and information furnished by the Client/Owner for above property and s/he confirmed all details is true in my presence.</p>
    </div>

    <div class="sec-h3" style="margin-top:4pt">7.5 &nbsp; Declaration by the Client</div>
    <div class="remarks-box" style="padding:6pt 10pt;font-size:10.5pt;line-height:1.35;text-align:justify;margin-bottom:0">
      <p style="margin:0">I/we have provided all the documents attached in this valuation report and all the details and information furnished above are true and correct. I take full responsibility in case of any liability occur regarding these documents. We, Owners/clients, hereby confirm that all the details and information furnished above are correct and represent the actual statements.</p>
      <div style="margin-top:10pt;display:flex;flex-wrap:wrap;gap:20pt">
        ${(s.clients||[]).map((cl,ci) => {
          const name    = (cl.showPerson && cl.person?.name)     ? cl.person.name
                        : (cl.showCompany && cl.company?.name)   ? cl.company.name
                        : `Client ${ci+1}`;
          const contact = (cl.showPerson && cl.person?.contact)  ? cl.person.contact
                        : (cl.showCompany && cl.company?.contact) ? cl.company.contact
                        : '';
          return `<div style="text-align:center;font-family:'Times New Roman',Times,serif;font-size:10.5pt;line-height:1.6">
            <div style="border-top:1pt solid #000;padding-top:4pt;min-width:140pt">
              <div style="font-weight:bold">${esc(name)}</div>
              <div style="font-size:9.5pt;color:#444">${esc(contact, 'Contact Number')}</div>
            </div>
          </div>`;
        }).join('')}
      </div>
    </div>

    <div class="sec-h3" style="margin-top:4pt">7.6 &nbsp; Certification</div>
    <div class="remarks-box" style="padding:6pt 10pt;font-size:10.5pt;line-height:1.35;text-align:justify;margin-bottom:0">
      I/We hereby certify that Valuation of the Properties as detailed in this report has been carried out by me/us in strict Compliance with Valuation Guidelines of <strong>${esc(s.bank, "[Bank Name]")}</strong>. The said property is an acceptable security to bank in all respects.
    </div>

    <div class="sec-h3" style="margin-top:4pt">7.7 &nbsp; Conclusions</div>
    <div class="remarks-box" style="padding:6pt 10pt;font-size:10.5pt;line-height:1.35;text-align:justify;margin-bottom:0">
      <p style="margin:0">This valuation was conducted for the purpose of establishing Fair Market and Distress Values of the said property for Client and Bank for mortgaging these properties. It is not to be used for any other purpose, and no part of this report is to be disseminated to the public or third parties.
      We certify that our firm is fully authorized to carry out the valuation work under the prevalent laws and we are fully equipped and competent to carry out the assignment and have the necessary qualifications, skills and experience required for the same.
      We also certify that no individual in our firm has any financial interest in the said property.
      To the best of our knowledge, all matters of a factual nature discussed in this report are true and correct. No important factors have been intentionally overlooked or withheld.
      We have physically inspected, verified and measured the properties in the presence of the Client/Representative of the Client.
      We transformed all details and information furnished by the Client/Owner for above property and s/he confirmed all details is true in my presence.</p>
    </div>

    <div style="margin-top:20pt;display:flex;justify-content:flex-end">
      <div style="text-align:center;font-family:'Times New Roman',Times,serif;font-size:11pt;line-height:1.7">
        <div style="border-top:1pt solid #000;padding-top:5pt;min-width:180pt">
          <div style="font-weight:bold">For ${esc(vi.company, "Neo-Civic Consulting (P). Ltd")}</div>
          <div style="margin-top:2pt">${esc(vi.name, "Er. Saakar Rimal")}</div>
          <div>NEC Registration No.: ${esc(vi.licenseNo, '11518 Civil "A"')}</div>
          ${vi.phone?`<div style="font-size:10pt">Tel. ${esc(vi.phone)}</div>`:""}
          ${vi.email?`<div style="font-size:10pt">Email: ${esc(vi.email)}</div>`:""}
        </div>
      </div>
    </div>

    <p class="disclaimer">Final Report &nbsp;|&nbsp; Generated: ${new Date().toLocaleDateString("en-NP",{year:"numeric",month:"long",day:"numeric"})} &nbsp;|&nbsp; ${esc(suggestedFilename, "")} &nbsp;|&nbsp; Prepared for the exclusive use of ${esc(s.bank, "the Bank")}.</p>
  </div>
  <!-- PHOTO PAGES (LANDSCAPE) -->
  ${final_photoPages}

  <!-- LEGAL DOCUMENTS SECTION -->
  ${(()=>{
    const docs = (s.legalDocs||[]).filter(d => d.dataUrl && d.category);
    if(!docs.length) return '';

    const CAT_LABELS = {
      govt_rate:'Government Rate', lorc:'LORC', citizenship:'Citizenship',
      trace:'Trace', charkilla:'Charkilla', tiro_receipt:'Tiro Receipt',
      company_doc:'Company Document', building_approval:'Building Approval',
      completion_cert:'Building Completion Certificate',
      building_drawing:'Building Drawing', other:'Other Documents',
    };
    const catLabel = (key) => CAT_LABELS[key] || esc(key) || '—';

    const separator = `<div class="legal-doc-sep" id="toc-s12">
      <div style="text-align:center;font-family:'Times New Roman',Times,serif;">
        <div style="width:80mm;height:3pt;background:#2c1810;margin:0 auto 12pt;"></div>
        <div style="font-size:22pt;font-weight:bold;text-transform:uppercase;letter-spacing:4px;color:#2c1810;">
          Legal Documents
        </div>
        <div style="font-size:11pt;color:#666;margin-top:6pt;letter-spacing:1px;">
          ${docs.length} document${docs.length !== 1 ? "s" : ""}
        </div>
        <div style="width:80mm;height:3pt;background:#2c1810;margin:12pt auto 0;"></div>
      </div>
    </div>`;

    const grouped = {};
    docs.forEach((doc,i) => {
      const k = doc.category || 'other';
      if (!grouped[k]) grouped[k] = [];
      grouped[k].push({...doc, _idx: i+1});
    });
    const indexRows = Object.entries(grouped).map(([key, gdocs]) =>
      gdocs.map((doc, gi) => `
        <tr style="background:${gi%2===0?'#fff':'#f9f6f3'}">
          <td style="padding:5pt 8pt;border:0.5pt solid #ccc;text-align:center;color:#2c1810;font-weight:bold;width:6%">${doc._idx}</td>
          <td style="padding:5pt 8pt;border:0.5pt solid #ccc;font-weight:bold;color:#2c1810">${catLabel(key)}</td>
        </tr>`).join('')
    ).join('');

    const indexPage = `<div class="legal-doc-page">
      <div style="font-size:14pt;font-weight:bold;text-transform:uppercase;letter-spacing:2px;color:#2c1810;border-bottom:2pt solid #2c1810;padding-bottom:5pt;margin-bottom:10pt;font-family:'Times New Roman',Times,serif;">
        Legal Documents — Index
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:9.5pt;">
        <thead><tr>
          <th style="background:#2c1810;color:#fff;padding:5pt 7pt;text-align:center;width:6%">No.</th>
          <th style="background:#2c1810;color:#fff;padding:5pt 7pt;text-align:left">Category</th>
        </tr></thead>
        <tbody>${indexRows}</tbody>
      </table>
    </div>`;

    const docImagePages = docs.map((doc,i)=>{
      const label = `${i+1}. ${catLabel(doc.category)}`;
      let body;
      if (doc.dataUrl.startsWith('data:image')) {
        const rot = doc.rotation || 0;
        const is90or270 = rot === 90 || rot === 270;
        // For 90/270 rotations we swap the container dimensions so the image fits within the page width
        const imgStyle = rot === 0
          ? `max-width:100%;max-height:170mm;object-fit:contain;display:block;margin:0 auto;`
          : is90or270
            ? `max-height:100%;max-width:170mm;object-fit:contain;display:block;margin:0 auto;transform:rotate(${rot}deg);transform-origin:center center;`
            : `max-width:100%;max-height:170mm;object-fit:contain;display:block;margin:0 auto;transform:rotate(${rot}deg);transform-origin:center center;`;
        const containerStyle = is90or270
          ? `text-align:center;border:0.75pt solid #ccc;background:#fafafa;padding:6pt;overflow:hidden;display:flex;align-items:center;justify-content:center;min-height:120mm;`
          : `text-align:center;border:0.75pt solid #ccc;background:#fafafa;padding:6pt;`;
        body = `<div style="${containerStyle}">
          <img src="${doc.dataUrl}" style="${imgStyle}"/>
        </div>`;
      } else if (doc.dataUrl.startsWith('data:application/pdf')) {
        body = `<div style="width:100%;height:170mm;border:0.75pt solid #ccc;overflow:hidden;">
          <object data="${doc.dataUrl}" type="application/pdf"
            style="width:100%;height:100%;display:block;">
            <iframe src="${doc.dataUrl}"
              style="width:100%;height:100%;border:none;display:block;">
              <p style="padding:20pt;text-align:center;color:#888;font-style:italic;">
                PDF: ${esc(doc.name, 'Document')} — open in PDF viewer to view
              </p>
            </iframe>
          </object>
        </div>`;
      } else {
        body = `<div style="border:0.75pt solid #ccc;padding:30pt;text-align:center;color:#888;font-style:italic;font-family:'Times New Roman',Times,serif;">
          Document: ${esc(doc.name, 'File')}
        </div>`;
      }
      return `
      <div class="legal-doc-page">
        <div style="font-family:'Times New Roman',Times,serif;margin-bottom:8pt;">
          <div style="background:#2c1810;color:#fff;padding:5pt 10pt;font-weight:bold;font-size:10.5pt;">
            ${label}
          </div>
        </div>
        ${body}
      </div>`;
    }).join('');

    return separator + indexPage + docImagePages;
  })()}

  ${purifyScript}${autoScript}${tocScript}</body></html>`;
}